import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { IntegrationService } from '../../services/IntegrationService';
import { PatientService } from '../../services/PatientService';
import { DoctorService } from '../../services/DoctorService';
import { DocumentVerificationService } from '../../services/DocumentVerificationService';
import { supabase } from '../../config/supabase';
import { Search, FileText, Eye, Download, CreditCard as Edit, Save, X, Filter, Printer, PlusCircle, Copy, AlertTriangle, History, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, ChevronDown, ChevronRight, ChevronUp, ShieldCheck, Link2, CalendarDays } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import InvalidateEvolutionModal from '../../components/admin/InvalidateEvolutionModal';
import AppointmentConflictResolutionModal, {
  type AppointmentConflictCandidate,
} from '../../components/admin/AppointmentConflictResolutionModal';
import PatientSearchSelect from '../../components/common/PatientSearchSelect';
import PaginationControls from '../../components/common/PaginationControls';
import { matchesPatientSearch, normalizeSearchText } from '../../utils/patientSearch';
import InitialAnamnesisPanel from '../../components/admin/InitialAnamnesisPanel';
import PatientClinicalTimeline from '../../components/admin/PatientClinicalTimeline';
import ClinicalRecordShortcutMenu from '../../components/admin/ClinicalRecordShortcutMenu';
import ActionButton from '../../components/ui/ActionButton';
import EvolutionVitalSignsField from '../../components/admin/EvolutionVitalSignsField';
import { SupabaseMedicalRecordRow } from '../../types/supabase'; // Importar tipos para medical_records
import { UnifiedPatient } from '../../types/unified-financial'; // Importar UnifiedPatient
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js'; // Importar tipo para realtime
import { AppointmentStatusService } from '../../services/AppointmentStatusService';
import {
  buildInvalidatedMedicalRecordAttachments,
  getMedicalRecordAttachmentsObject,
  getMedicalRecordLifecycleStatus,
  isMedicalRecordInvalidated,
} from '../../utils/medicalRecordStatus';
import {
  buildMedicalRecordAuthorSnapshot,
  fetchMedicalRecordAuthors,
  getMedicalRecordAuthorFromAttachments,
  hasNonApplicableProfessionalRegistration,
  mergeMedicalRecordAuthorInfo,
  requiresProfessionalRegistration,
  type MedicalRecordAuthorInfo,
} from '../../utils/medicalRecordAuthor';
import {
  buildSignedDocumentMetadata,
  buildVerificationUrl,
  createVerificationQrDataUrl,
  generateVerificationToken,
  getMedicalRecordSignatureMetadata,
  hashBinaryContent,
  hashVerificationPayload,
  type SignedDocumentMetadata,
} from '../../utils/documentVerification';
import clinicInfo from '../../config/clinicInfo';
import { ClientReportSettingsService } from '../../features/assessments-v2/services/ClientReportSettingsService';
import type { ResolvedClientReportBranding } from '../../features/assessments-v2/types';
import type { AgendaEventIntegrated } from '../../types/integration';
import { isClinicalHistoryRecord } from '../../utils/clinicalDocuments';
import {
  areMedicalRecordVitalSignsEqual,
  createEmptyMedicalRecordVitalSignsDraft,
  formatMedicalRecordVitalSigns,
  getMedicalRecordVitalSigns,
  medicalRecordVitalSignsToDraft,
  parseMedicalRecordVitalSignsDraft,
  type MedicalRecordVitalSigns,
  type MedicalRecordVitalSignsDraft,
} from '../../utils/medicalRecordVitalSigns';
import { buildTenantPath } from '../../utils/tenantSlug';

async function toDataUrl(url?: string | null): Promise<string | null> {
  if (!url) return null;

  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Não foi possível carregar imagem para impressão do prontuário:', error);
    return null;
  }
}

const PRINT_CAPTURE_WIDTH_PX = 794;
const PRINT_PAGE_TOTAL_HEIGHT_PX = 1123;
const PRINT_PAGE_VERTICAL_PADDING_PX = 40;
const PRINT_PAGE_CONTENT_MAX_HEIGHT_PX = PRINT_PAGE_TOTAL_HEIGHT_PX - PRINT_PAGE_VERTICAL_PADDING_PX;

const waitForPdfRender = () =>
  new Promise<void>((resolve) => {
    window.setTimeout(() => resolve(), 120);
  });

const createPrintTextBlocks = (text: string, maxBlockChars = 420): string[] => {
  const normalized = String(text || '')
    .replace(/\r\n/g, '\n')
    .trim();

  if (!normalized) {
    return ['Nenhuma evolução registrada'];
  }

  const splitOversizedBlock = (block: string): string[] => {
    if (block.length <= maxBlockChars) {
      return [block];
    }

    const sentenceParts = block.split(/(?<=[.!?])\s+/).filter(Boolean);
    if (sentenceParts.length > 1) {
      const grouped: string[] = [];
      let current = '';

      for (const sentence of sentenceParts) {
        const candidate = current ? `${current} ${sentence}` : sentence;
        if (candidate.length > maxBlockChars && current) {
          grouped.push(current);
          current = sentence;
        } else {
          current = candidate;
        }
      }

      if (current) {
        grouped.push(current);
      }

      return grouped.flatMap((item) => splitOversizedBlock(item));
    }

    const wordParts = block.split(/\s+/).filter(Boolean);
    const grouped: string[] = [];
    let current = '';

    for (const word of wordParts) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > maxBlockChars && current) {
        grouped.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }

    if (current) {
      grouped.push(current);
    }

    return grouped.length ? grouped : [block];
  };

  const blocks = normalized
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean)
    .flatMap((item) => splitOversizedBlock(item));

  return blocks.length ? blocks : ['Nenhuma evolução registrada'];
};

const createDocumentPageMeasurer = async (documentStyles: string) => {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.left = '-10000px';
  wrapper.style.top = '0';
  wrapper.style.width = `${PRINT_CAPTURE_WIDTH_PX}px`;
  wrapper.style.background = '#ffffff';
  wrapper.style.zIndex = '-1';
  wrapper.innerHTML = `
    ${documentStyles}
    <div class="capture-root">
      <section class="document-page">
        <div class="document-container"></div>
      </section>
    </div>
  `;
  document.body.appendChild(wrapper);

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
  await waitForPdfRender();

  const container = wrapper.querySelector('.document-container') as HTMLElement;

  return {
    measure(blocks: string[]) {
      container.innerHTML = blocks.join('');
      return Math.ceil(container.getBoundingClientRect().height);
    },
    destroy() {
      document.body.removeChild(wrapper);
    },
  };
};

const paginateDocumentBlocks = async (input: {
  documentStyles: string;
  firstPagePrefix: string[];
  continuationPagePrefix: string[];
  textBlocks: string[];
  buildFlowBlockHtml: (text: string, isContinuationPage: boolean) => string;
  finalCompositeBlock?: string | null;
}): Promise<string[]> => {
  const measurer = await createDocumentPageMeasurer(input.documentStyles);

  try {
    const pages: string[][] = [];
    let currentPagePrefix = [...input.firstPagePrefix];
    let isContinuationPage = false;
    let currentTextBlocks: string[] = [];

    const buildCurrentFlowBlock = (textBlocks: string[], continuation: boolean) =>
      input.buildFlowBlockHtml(textBlocks.join('\n\n'), continuation);

    const flushTextPage = () => {
      if (currentTextBlocks.length === 0) {
        return;
      }

      pages.push([
        ...currentPagePrefix,
        buildCurrentFlowBlock(currentTextBlocks, isContinuationPage),
      ]);
      currentPagePrefix = [...input.continuationPagePrefix];
      isContinuationPage = true;
      currentTextBlocks = [];
    };

    for (const block of input.textBlocks) {
      const candidateTextBlocks = [...currentTextBlocks, block];
      const candidatePageBlocks = [
        ...currentPagePrefix,
        buildCurrentFlowBlock(candidateTextBlocks, isContinuationPage),
      ];
      const fitsCurrentPage =
        measurer.measure(candidatePageBlocks) <= PRINT_PAGE_CONTENT_MAX_HEIGHT_PX;

      if (fitsCurrentPage || currentTextBlocks.length === 0) {
        currentTextBlocks = candidateTextBlocks;
        continue;
      }

      flushTextPage();
      currentTextBlocks = [block];
    }

    let finalPageBlocks =
      currentTextBlocks.length > 0
        ? [...currentPagePrefix, buildCurrentFlowBlock(currentTextBlocks, isContinuationPage)]
        : [...currentPagePrefix];

    if (input.finalCompositeBlock) {
      const candidateWithFinal = [...finalPageBlocks, input.finalCompositeBlock];
      const finalFitsCurrentPage =
        measurer.measure(candidateWithFinal) <= PRINT_PAGE_CONTENT_MAX_HEIGHT_PX;

      if (finalFitsCurrentPage) {
        finalPageBlocks = candidateWithFinal;
      } else {
        if (finalPageBlocks.length > 0) {
          pages.push(finalPageBlocks);
        }
        finalPageBlocks = [...input.continuationPagePrefix, input.finalCompositeBlock];
      }
    }

    pages.push(finalPageBlocks);

    return pages.map(
      (pageBlocks) => `
        <section class="document-page">
          <div class="document-container">
            ${pageBlocks.join('')}
          </div>
        </section>
      `,
    );
  } finally {
    measurer.destroy();
  }
};

const renderDocumentPagesToPdf = async (
  documentPages: string[],
  documentStyles: string,
): Promise<jsPDF> => {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.left = '-10000px';
  wrapper.style.top = '0';
  wrapper.style.width = `${PRINT_CAPTURE_WIDTH_PX}px`;
  wrapper.style.background = '#ffffff';
  wrapper.style.zIndex = '-1';
  wrapper.innerHTML = `${documentStyles}<div class="capture-root">${documentPages.join('')}</div>`;
  document.body.appendChild(wrapper);

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    await waitForPdfRender();

    const pageElements = Array.from(wrapper.querySelectorAll('.document-page')) as HTMLElement[];
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    if (pageElements.length === 0) {
      return pdf;
    }

    for (let index = 0; index < pageElements.length; index += 1) {
      const pageElement = pageElements[index];
      const canvas = await html2canvas(pageElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: PRINT_CAPTURE_WIDTH_PX,
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      if (index > 0) {
        pdf.addPage();
      }
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;

      while (heightLeft > 0.5) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }
    }

    const totalPages = pdf.getNumberOfPages();
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(125, 140, 160);

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      pdf.setPage(pageNumber);
      pdf.text(`${pageNumber}/${totalPages}`, pageWidth - 10, pageHeight - 5, {
        align: 'right',
        baseline: 'bottom',
      });
    }

    return pdf;
  } finally {
    document.body.removeChild(wrapper);
  }
};

const openPdfBytesForPrint = (pdfBytes: Uint8Array) => {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');

  if (printWindow) {
    printWindow.onload = () => {
      window.setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
          URL.revokeObjectURL(url);
        };
      }, 500);
    };
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  iframe.style.width = '0px';
  iframe.style.height = '0px';
  document.body.appendChild(iframe);
  iframe.src = url;

  window.setTimeout(() => {
    iframe.contentWindow?.print();
    window.setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 1000);
  }, 500);
};

// Interface para informações de invalidação
// Interface para informações de invalidação (adaptada para Supabase)
interface InvalidationInfo {
  timestamp: string; // ISO string
  user_uid: string;
  user_name: string;
  user_email: string;
  justification: string;
}

type InvalidationResolutionStatus = 'unique' | 'not_found' | 'conflict';

interface InvalidationPreviewResult {
  resolutionStatus?: InvalidationResolutionStatus;
  candidateByEvolutionId?: AppointmentConflictCandidate | null;
  candidateByAttachmentId?: AppointmentConflictCandidate | null;
  conflictCandidates?: AppointmentConflictCandidate[] | null;
}

interface InvalidationAtomicConfirmResult {
  medicalRecordUpdated?: boolean;
  appointmentUpdated?: boolean;
  usedAppointmentId?: string | null;
  resolutionPath?: string;
  updatedRecordAttachments?: Record<string, any>;
}

type AppointmentScope = 'mine' | 'all';
type EvolutionEditMode = 'clinical' | 'appointment';
type MedicalRecordView = 'evolutions' | 'timeline';

// Interface para dados do prontuário (baseada em SupabaseMedicalRecordRow)
export interface ProntuarioData {
  id: string; // recordId
  patientId: string; // FK para patients
  doctorId?: string;
  recordDate: string; // Data e hora da consulta
  recordDescription: string; // Campo de evolução/observações
  recordCreatedAt?: string; // Data de criação
  recordUpdatedAt?: string; // Data de atualização
  // Campos otimizados
  patientFullName?: string;
  medicalRecordNumber?: string; // Numero do prontuario
  // Campos para soft delete
  status?: 'active' | 'invalidated'; // Mapeado de recordType/metadata
  invalidation_info?: InvalidationInfo;
  invalidation_history?: InvalidationInfo[];
  signature?: SignedDocumentMetadata | null;
  vitalSigns?: MedicalRecordVitalSigns | null;
  // Para compatibilidade com o agendamentoId existente no Firebase
  agendamentoId?: string;
  author?: MedicalRecordAuthorInfo | null;
  [key: string]: any;
}

const ProntuarioPage: React.FC = () => {
  const { user, selectedClient } = useAuth(); // Obter selectedClient
  const location = useLocation();
  const [allProntuarios, setAllProntuarios] = useState<ProntuarioData[]>([]);
  // Manteremos patients como UnifiedPatient[] e adaptaremos PatientSearchSelect ou mappedPatients
  const [patients, setPatients] = useState<UnifiedPatient[]>([]); 
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProntuario, setSelectedProntuario] = useState<ProntuarioData | null>(null);
  const [editingProntuario, setEditingProntuario] = useState<ProntuarioData | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProntuarioData>>({});
  const [editVitalSignsDraft, setEditVitalSignsDraft] = useState<MedicalRecordVitalSignsDraft>(() =>
    createEmptyMedicalRecordVitalSignsDraft(),
  );
  const [filterPatient, setFilterPatient] = useState<string>('all');
  const [filterProfessional, setFilterProfessional] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [connectionError, setConnectionError] = useState<string>('');
  const [medicalRecordView, setMedicalRecordView] = useState<MedicalRecordView>('evolutions');
  const [timelineRefreshVersion, setTimelineRefreshVersion] = useState(0);

  const storageKey = 'pagination_prontuario_page_pageSize';
  const savedPageSize = localStorage.getItem(storageKey);
  const initialPageSize = savedPageSize === 'all' ? 'all' : (savedPageSize ? parseInt(savedPageSize) : 50);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'all'>(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);
  
  // Estados para novo prontuário
  const [showNewProntuarioModal, setShowNewProntuarioModal] = useState(false);
  const [newProntuarioForm, setNewProntuarioForm] = useState({
    selectedPatientId: '',
    datetime: (() => {
      // ✅ CORREÇÃO: Usar fuso horário brasileiro (São Paulo)
      const now = new Date();
      const brasiliaOffset = -3 * 60; // UTC-3 (horário de Brasília)
      const brasiliaTime = new Date(now.getTime() + (brasiliaOffset * 60 * 1000));
      return brasiliaTime.toISOString().slice(0, 16);
    })(),
    evolution: '',
    selectedAppointmentId: '' // 🆕 Novo campo para vincular agendamento
  });
  const [newEvolutionVitalSignsDraft, setNewEvolutionVitalSignsDraft] = useState<MedicalRecordVitalSignsDraft>(() =>
    createEmptyMedicalRecordVitalSignsDraft(),
  );
  const [lastEvolution, setLastEvolution] = useState<string>('');
  const [showLastEvolution, setShowLastEvolution] = useState(false);
  const [showLastEvolutionDetails, setShowLastEvolutionDetails] = useState(false);
  const [showAppointmentPicker, setShowAppointmentPicker] = useState(false);
  const [isSubmittingProntuario, setIsSubmittingProntuario] = useState(false);
  const [appointmentScope, setAppointmentScope] = useState<AppointmentScope>('mine');
  const [currentDoctorId, setCurrentDoctorId] = useState<string | null>(null);
  const [patientAppointments, setPatientAppointments] = useState<AgendaEventIntegrated[]>([]); // 🆕 Lista de agendamentos do paciente
  const [loadingAppointments, setLoadingAppointments] = useState(false); // 🆕 Loading de agendamentos
  const [categorizedAppointments, setCategorizedAppointments] = useState<{
    available: AgendaEventIntegrated[];
    linked: AgendaEventIntegrated[];
    currentLink: AgendaEventIntegrated | null;
  }>({ available: [], linked: [], currentLink: null }); // 🆕 Agendamentos categorizados
  const [invalidatingEvolution, setInvalidatingEvolution] = useState<ProntuarioData | null>(null);
  const [appointmentConflictState, setAppointmentConflictState] = useState<{
    candidates: AppointmentConflictCandidate[];
    recommendedAppointmentId?: string | null;
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'invalidated'>('active');
  const [sortColumn, setSortColumn] = useState<string>('data');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newItemsCount, setNewItemsCount] = useState(0);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [signingEvolutionId, setSigningEvolutionId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<EvolutionEditMode>('clinical');
  const [showBatchPrintSelection, setShowBatchPrintSelection] = useState(false);
  const [selectedBatchPrintIds, setSelectedBatchPrintIds] = useState<string[]>([]);
  const [isBatchPrinting, setIsBatchPrinting] = useState(false);
  const previousCountRef = useRef<number>(0);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const conflictResolverRef = useRef<((value: string | null) => void) | null>(null);
  const appointmentPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showAppointmentPicker) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (appointmentPickerRef.current && !appointmentPickerRef.current.contains(event.target as Node)) {
        setShowAppointmentPicker(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowAppointmentPicker(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showAppointmentPicker]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrentDoctorId = async () => {
      if (!user?.userId) {
        setCurrentDoctorId(null);
        return;
      }

      try {
        const doctor = await DoctorService.getDoctorByuserId(user.userId);
        if (isMounted) {
          setCurrentDoctorId(doctor?.doctorId || null);
        }
      } catch (error) {
        console.warn('Não foi possível resolver doctorId do usuário atual para filtro de agendamentos:', error);
        if (isMounted) {
          setCurrentDoctorId(null);
        }
      }
    };

    void loadCurrentDoctorId();

    return () => {
      isMounted = false;
    };
  }, [user?.userId]);

  const isAppointmentFromCurrentUser = useCallback(
    (appointment: AgendaEventIntegrated) => {
      const currentUserId = user?.userId;
      return Boolean(
        (currentUserId && appointment.userId === currentUserId) ||
        (currentDoctorId && appointment.doctorId === currentDoctorId),
      );
    },
    [currentDoctorId, user?.userId],
  );

  const shouldShowAppointmentForScope = useCallback(
    (appointment: AgendaEventIntegrated) => {
      if (appointmentScope === 'all') return true;
      if (isAppointmentFromCurrentUser(appointment)) return true;
      return appointment.id === newProntuarioForm.selectedAppointmentId;
    },
    [appointmentScope, isAppointmentFromCurrentUser, newProntuarioForm.selectedAppointmentId],
  );

  const visibleCategorizedAppointments = useMemo(() => {
    const available = categorizedAppointments.available.filter(shouldShowAppointmentForScope);
    const linked = categorizedAppointments.linked.filter(shouldShowAppointmentForScope);
    const currentLink = categorizedAppointments.currentLink &&
      shouldShowAppointmentForScope(categorizedAppointments.currentLink)
      ? categorizedAppointments.currentLink
      : null;

    return { available, linked, currentLink };
  }, [categorizedAppointments, shouldShowAppointmentForScope]);

  const visiblePatientAppointmentsCount = useMemo(() => {
    const ids = new Set<string>();
    if (visibleCategorizedAppointments.currentLink) {
      ids.add(visibleCategorizedAppointments.currentLink.id);
    }
    visibleCategorizedAppointments.available.forEach((appointment) => ids.add(appointment.id));
    visibleCategorizedAppointments.linked.forEach((appointment) => ids.add(appointment.id));
    return ids.size;
  }, [visibleCategorizedAppointments]);

  const selectedAppointment = useMemo(() => {
    const selectedId = newProntuarioForm.selectedAppointmentId;
    if (!selectedId || selectedId === 'none') return null;
    return patientAppointments.find((appointment) => appointment.id === selectedId) || null;
  }, [newProntuarioForm.selectedAppointmentId, patientAppointments]);

  const hasAppointmentsFromCurrentUser = useMemo(
    () => patientAppointments.some(isAppointmentFromCurrentUser),
    [isAppointmentFromCurrentUser, patientAppointments],
  );

  const applyInvalidationToLocalState = useCallback(
    (
      evolutionId: string,
      invalidationData: InvalidationInfo,
      updatedAttachments: Record<string, any>,
      usedAppointmentId: string | null,
    ) => {
      const updateProntuario = (prontuario: ProntuarioData): ProntuarioData =>
        prontuario.id === evolutionId
          ? {
              ...prontuario,
              status: 'invalidated',
              recordUpdatedAt: new Date().toISOString(),
              invalidation_info: invalidationData,
              invalidation_history: updatedAttachments.invalidation_history,
              agendamentoId: null,
              appointmentUnlinkInfo: updatedAttachments.appointmentUnlinkInfo,
              lastInvalidationResolutionPath: updatedAttachments.lastInvalidationResolutionPath,
            }
          : prontuario;

      setAllProntuarios((prev) => prev.map(updateProntuario));
      setSelectedProntuario((prev) => (prev ? updateProntuario(prev) : prev));
      setInvalidatingEvolution((prev) => (prev ? updateProntuario(prev) : prev));
      if (usedAppointmentId) {
        setPatientAppointments((prev) =>
          prev.map((appointment) =>
            appointment.id === usedAppointmentId
              ? {
                  ...appointment,
                  status: 'no_evaluated',
                  appointmentStatus: 'no_evaluated',
                  appointmentEvolutionId: null,
                }
              : appointment,
          ),
        );
      }
    },
    [],
  );

  const openConflictResolutionDialog = useCallback(
    (candidates: AppointmentConflictCandidate[], recommendedAppointmentId?: string | null) =>
      new Promise<string | null>((resolve) => {
        conflictResolverRef.current = resolve;
        setAppointmentConflictState({
          candidates,
          recommendedAppointmentId: recommendedAppointmentId || null,
        });
      }),
    [],
  );

  const resolveConflictSelection = useCallback((appointmentId: string | null) => {
    if (conflictResolverRef.current) {
      conflictResolverRef.current(appointmentId);
      conflictResolverRef.current = null;
    }
    setAppointmentConflictState(null);
  }, []);

  useEffect(() => {
    return () => {
      if (conflictResolverRef.current) {
        conflictResolverRef.current(null);
        conflictResolverRef.current = null;
      }
    };
  }, []);

  const getPatientLifecycleStatus = (patient: any): 'active' | 'inactive' | 'archived' => {
    const status = patient?.patientLifecycleStatus ?? patient?.lifecycleStatus ?? 'active';
    if (status === 'inactive' || status === 'archived') return status;
    return 'active';
  };

  const getPatientNewCareBlockReason = (patient: any): 'lifecycle' | 'clinical_inactive' | null => {
    const lifecycleStatus = getPatientLifecycleStatus(patient);
    if (lifecycleStatus !== 'active') return 'lifecycle';
    const financialActive = typeof patient?.financialProfile?.active === 'boolean'
      ? patient.financialProfile.active
      : true;
    if (!financialActive) return 'clinical_inactive';
    return null;
  };

  // Função para ordenar prontuários
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Função para obter ícone de ordenação
  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ?
      <ArrowUp className="w-4 h-4 text-primary-600" /> :
      <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  // Função para formatar data (adaptada para o formato correto do Supabase)
  const formatDate = (dateValue: any, includeTime: boolean = false): string => {
    if (!dateValue) return 'N/A';
    
    try {
      let date: Date;
      
      // Se for uma string (ISO ou outra formatacao), tenta parsear
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return 'N/A';
      }
      
      // Verifica se a data é válida
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      if (includeTime) {
        return date.toLocaleString('pt-BR');
      } else {
        return date.toLocaleDateString('pt-BR');
      }
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'N/A';
    }
  };

  const formatCompactDate = (dateValue: any): string => {
    if (!dateValue) return 'N/A';

    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'N/A';
      }

      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Erro ao formatar data compacta:', error);
      return 'N/A';
    }
  };


  // Função para extrair texto do HTML
  const extractTextFromHTML = (htmlString: string): string => {
    if (!htmlString || typeof htmlString !== 'string') {
      return 'Não informado';
    }
    
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      return textContent.trim() || 'Não informado';
    } catch (error) {
      console.error('Erro ao extrair texto do HTML:', error);
      return 'Não informado';
    }
  };

  // Função para imprimir uma ou várias evoluções
  const printEvolutions = async (prontuarios: ProntuarioData[]) => {
    if (prontuarios.length === 0) {
      toast.info('Selecione ao menos uma evolução para imprimir.');
      return;
    }

    if (!selectedClient?.clientId) {
      toast.error('Cliente não identificado para consultar os agendamentos.');
      return;
    }

    const linkedAppointmentIds = Array.from(
      new Set(
        prontuarios
          .map((prontuario) => prontuario.agendamentoId)
          .filter((appointmentId): appointmentId is string => Boolean(appointmentId)),
      ),
    );
    const evolutionIds = prontuarios.map((prontuario) => prontuario.id);

    const [appointmentsByIdResult, appointmentsByEvolutionResult] = await Promise.all([
      linkedAppointmentIds.length > 0
        ? supabase
            .from('appointments')
            .select('appointmentId, appointmentStartDate, appointmentEvolutionId')
            .eq('clientId', selectedClient.clientId)
            .in('appointmentId', linkedAppointmentIds)
        : Promise.resolve({ data: [], error: null }),
      supabase
        .from('appointments')
        .select('appointmentId, appointmentStartDate, appointmentEvolutionId')
        .eq('clientId', selectedClient.clientId)
        .in('appointmentEvolutionId', evolutionIds),
    ]);

    if (appointmentsByIdResult.error || appointmentsByEvolutionResult.error) {
      console.error(
        'Erro ao consultar o horário agendado para impressão da evolução:',
        appointmentsByIdResult.error || appointmentsByEvolutionResult.error,
      );
      toast.error('Não foi possível consultar o horário agendado. Tente imprimir novamente.');
      return;
    }

    const appointmentsById = new Map(
      (appointmentsByIdResult.data || []).map((appointment) => [appointment.appointmentId, appointment]),
    );
    const appointmentsByEvolution = new Map(
      (appointmentsByEvolutionResult.data || [])
        .filter((appointment) => Boolean(appointment.appointmentEvolutionId))
        .map((appointment) => [appointment.appointmentEvolutionId as string, appointment]),
    );
    const appointmentStartByEvolutionId = new Map<string, string>();

    prontuarios.forEach((prontuario) => {
      const appointment =
        (prontuario.agendamentoId ? appointmentsById.get(prontuario.agendamentoId) : undefined) ||
        appointmentsByEvolution.get(prontuario.id);

      if (appointment?.appointmentStartDate) {
        appointmentStartByEvolutionId.set(prontuario.id, appointment.appointmentStartDate);
      }
    });

    const sortedProntuarios = [...prontuarios].sort(
      (a, b) => new Date(a.recordDate || 0).getTime() - new Date(b.recordDate || 0).getTime(),
    );
    const activeClientId = selectedClient?.clientId || user?.current_client_id || '';
    const tenantSlug = selectedClient?.clientSlug || null;
    const printedAt = new Date().toLocaleString('pt-BR');
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    const sanitizeFileNamePart = (value: string) =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();

    const fallbackBranding: ResolvedClientReportBranding = {
      clientId: activeClientId,
      source: 'fallback',
      displayName: selectedClient?.clientName || clinicInfo.nomeFantasia,
      legalName: clinicInfo.razaoSocial,
      cnpj: clinicInfo.cnpj,
      reportSubtitle: clinicInfo.especialidades,
      logoPrintUrl:
        selectedClient?.clientLogoWordmarkUrl ||
        selectedClient?.logo_wordmark_url ||
        selectedClient?.clientLogoUrl ||
        selectedClient?.logo_url ||
        clinicInfo.logoPrint,
      logoMarkUrl:
        selectedClient?.clientLogoUrl ||
        selectedClient?.logo_url ||
        clinicInfo.logo,
      logoWordmarkUrl:
        selectedClient?.clientLogoWordmarkUrl ||
        selectedClient?.logo_wordmark_url ||
        selectedClient?.clientLogoUrl ||
        selectedClient?.logo_url ||
        clinicInfo.logoPrint,
      logoAlt: selectedClient?.clientName || clinicInfo.nomeFantasia,
      addressLine1: clinicInfo.endereco.enderecoCompleto,
      addressLine2: `${clinicInfo.endereco.cidade} - ${clinicInfo.endereco.estado} | CEP: ${clinicInfo.endereco.cep}`,
      contactLine: `${clinicInfo.contato.telefone} | ${clinicInfo.contato.email}`,
      footerLegalLine: `${clinicInfo.razaoSocial} - CNPJ: ${clinicInfo.cnpj}`,
      rawSettings: null,
    };

    const branding = activeClientId
      ? await ClientReportSettingsService.resolveForClient(activeClientId, selectedClient).catch((error) => {
          console.warn('Falha ao resolver branding do tenant para impressão do prontuário:', error);
          return fallbackBranding;
        })
      : fallbackBranding;

    const [logoDataUrl, revahubMarkDataUrl] = await Promise.all([
      toDataUrl(branding.logoPrintUrl || branding.logoWordmarkUrl || branding.logoMarkUrl),
      toDataUrl('/logos/revahub-mark.png'),
    ]);

    const safeBrandingDisplayName = escapeHtml(branding.displayName || branding.legalName || 'Relatório Clínico');
    const safeBrandingSubtitle = escapeHtml(branding.reportSubtitle || 'Relatório clínico individual');
    const safeBrandingFooterLegal = escapeHtml(
      branding.footerLegalLine || `${branding.legalName} - CNPJ: ${branding.cnpj}`,
    );
    const safeLogoAlt = escapeHtml(branding.logoAlt || branding.displayName || 'Logo do tenant');
    const safeBrandingInitial = escapeHtml((branding.displayName || branding.legalName || 'R').slice(0, 1).toUpperCase());
    const tenantContactHtml = [
      branding.addressLine1,
      branding.addressLine2,
      branding.contactLine,
      branding.cnpj && branding.cnpj !== '—' ? `CNPJ: ${branding.cnpj}` : '',
    ]
      .filter((line): line is string => Boolean(line && line.trim() && line !== '—'))
      .map((line) => `<div>${escapeHtml(line)}</div>`)
      .join('');
    const safePrintedAt = escapeHtml(printedAt);
    const documentTitlePatient = sortedProntuarios[0]?.patientFullName || 'Paciente';
    const canRegisterGeneratedFile =
      Boolean(activeClientId) &&
      sortedProntuarios.every((item) => Boolean(item.signature?.verificationToken));
    const generatedFileToken = canRegisterGeneratedFile ? generateVerificationToken('rvhf') : null;
    const generatedFileUrl = generatedFileToken
      ? buildVerificationUrl(generatedFileToken, tenantSlug)
      : '';
    const generatedFileDisplayUrl = generatedFileUrl.replace(/^https?:\/\//, '');
    const generatedFileQrDataUrl = generatedFileToken
      ? await createVerificationQrDataUrl(generatedFileUrl).catch(() => '')
      : '';
    const generatedAtIso = new Date().toISOString();

    const buildDocumentPages = async (documentStyles: string) => {
      const pageGroups = await Promise.all(
        sortedProntuarios.map(async (prontuario, index) => {
          const evolutionText = extractTextFromHTML(prontuario.recordDescription || '');
          const vitalSignsText = prontuario.vitalSigns
            ? formatMedicalRecordVitalSigns(prontuario.vitalSigns, { includeMeasuredAt: true })
            : null;
          const patientName = prontuario.patientFullName || 'Nome não disponível';
          const appointmentStart = appointmentStartByEvolutionId.get(prontuario.id);
          const attendanceDate = appointmentStart
            ? formatDate(appointmentStart, true)
            : 'Atendimento sem agendamento';
          const creationDate = formatDate(prontuario.recordCreatedAt, true);
          const author = prontuario.author || buildMedicalRecordAuthorSnapshot({});
          const signature = prontuario.signature ?? null;
          const isSigned = Boolean(signature?.verificationToken);
          const isInvalidated = prontuario.status === 'invalidated';
          const doctorName = author.name;
          const councilNumber = hasNonApplicableProfessionalRegistration(author.role)
            ? 'Registro profissional não aplicável'
            : author.councilNumber || 'Conselho não informado';
          const professionalTitle = author.professionalTitle;
          const signatureName = signature?.signedByName || doctorName;
          const signatureRole = signature?.signedByRole || professionalTitle;
          const signatureRegistration = signature?.signedByRegistration || councilNumber;
          const verificationUrl = generatedFileToken
            ? generatedFileUrl
            : signature
              ? buildVerificationUrl(signature.verificationToken, tenantSlug)
              : '';
          const verificationDisplayUrl = verificationUrl.replace(/^https?:\/\//, '');
          const verificationQrDataUrl = generatedFileToken
            ? generatedFileQrDataUrl
            : signature
              ? await createVerificationQrDataUrl(verificationUrl).catch(() => '')
              : '';
          const signedAtLabel = signature ? formatDate(signature.signedAt, true) : '';
          const titleBadge = isInvalidated
            ? 'Documento invalidado'
            : isSigned
              ? 'Documento assinado'
              : 'Documento pendente';
          const safePatientName = escapeHtml(patientName || 'Não informado');
          const safeVitalSignsText = vitalSignsText ? escapeHtml(vitalSignsText) : '';
          const safeMedicalRecordNumber = escapeHtml(prontuario.medicalRecordNumber || 'Não informado');
          const safeAttendanceDate = escapeHtml(attendanceDate);
          const safeCreationDate = escapeHtml(creationDate);
          const safeDoctorName = escapeHtml(signatureName);
          const safeProfessionalTitle = escapeHtml(signatureRole);
          const safeCouncilNumber = escapeHtml(signatureRegistration);
          const safeTitleBadge = escapeHtml(titleBadge);
          const safeVerificationUrl = escapeHtml(verificationDisplayUrl);
          const safeVerificationToken = escapeHtml(generatedFileToken || signature?.verificationToken || 'pendente');
          const safeSignedAtLabel = escapeHtml(signedAtLabel || 'pendente');
          const safeVerificationTitle = escapeHtml(
            generatedFileToken ? 'Autenticidade do arquivo PDF' : 'Autenticidade do documento',
          );
          const sequenceLabel =
            sortedProntuarios.length > 1
              ? `<span>Registro ${index + 1} de ${sortedProntuarios.length}</span>`
              : '';
          const statusBadge = `<span class="summary-pill status-pill">${safeTitleBadge}</span>`;
          const continuationFlag = `<span>Continuação</span>`;
          const textBlocks = createPrintTextBlocks(evolutionText, 460);

          const firstPagePrefix = [
            `<div class="page-accent"></div>`,
            `
              <div class="header">
                <div class="header-left">
                  <div class="logo-container">
                    ${logoDataUrl ? `<img src="${logoDataUrl}" alt="${safeLogoAlt}" />` : `<div class="logo-fallback">${safeBrandingInitial}</div>`}
                  </div>
                  <div class="brand-stack">
                    <div class="brand-kicker">Prontuário eletrônico</div>
                    <div class="tenant-title">${safeBrandingDisplayName}</div>
                    <div class="tenant-subtitle">${safeBrandingSubtitle}</div>
                  </div>
                </div>
              </div>
            `,
            `
              <div class="document-summary">
                <div class="document-summary-left">
                  <h1>Evolução Clínica</h1>
                </div>
                <div class="document-summary-meta">
                  ${statusBadge}
                  ${sequenceLabel}
                </div>
              </div>
            `,
            `
              <div class="record-meta-band">
                <div class="record-meta-primary">
                  <div class="record-meta-label">Paciente</div>
                  <div class="record-meta-name">${safePatientName}</div>
                </div>
                <div class="record-meta-grid">
                  <div class="record-meta-item record-meta-item-mrn">
                    <span class="record-meta-label">Prontuário</span>
                    <span class="record-meta-value">${safeMedicalRecordNumber}</span>
                  </div>
                  <div class="record-meta-item record-meta-item-attendance">
                    <span class="record-meta-label">Atendimento</span>
                    <span class="record-meta-value">${safeAttendanceDate}</span>
                  </div>
                  <div class="record-meta-item record-meta-item-created">
                    <span class="record-meta-label">Registro</span>
                    <span class="record-meta-value">${safeCreationDate}</span>
                  </div>
                  <div class="record-meta-item record-meta-item-professional">
                    <span class="record-meta-label">Responsável técnico</span>
                    <span class="record-meta-value">${safeDoctorName}</span>
                    <span class="record-meta-subvalue">${safeProfessionalTitle} • ${safeCouncilNumber}</span>
                  </div>
                </div>
              </div>
            `,
          ];

          const continuationPagePrefix = [
            `<div class="page-accent"></div>`,
            `
              <div class="continuation-banner">
                <div class="continuation-banner-left">
                  <div class="continuation-kicker">Evolução Clínica</div>
                  <div class="continuation-patient">${safePatientName}</div>
                </div>
                <div class="continuation-banner-meta">
                  ${statusBadge}
                  ${sequenceLabel}
                  ${continuationFlag}
                </div>
              </div>
            `,
          ];

          const finalCompositeBlock = `
            <div class="document-final-block">
              <div class="verification-section">
                <div class="verification-panel">
                  <div class="verification-qr">
                    ${verificationQrDataUrl
                      ? `<img src="${verificationQrDataUrl}" alt="QR code de autenticidade" />`
                      : `<div class="verification-placeholder"></div>`}
                  </div>
                  <div class="verification-content">
                    <div class="verification-title">${safeVerificationTitle}</div>
                    <div class="verification-copy">
                      ${isSigned
                        ? `
                          <div>Documento assinado eletronicamente por <strong>${safeDoctorName}</strong>.</div>
                          <div>${safeProfessionalTitle} • ${safeCouncilNumber}</div>
                          <div>Assinado em: <strong>${safeSignedAtLabel}</strong></div>
                          ${generatedFileToken ? `
                            <div>Impresso em: <strong>${safePrintedAt}</strong></div>
                          ` : ''}
                          <div>${generatedFileToken ? 'Código do arquivo' : 'Código do documento'}: <strong>${safeVerificationToken}</strong></div>
                          <div>Validação: <span class="verification-link">${safeVerificationUrl}</span></div>
                        `
                        : `
                          <div>Documento ainda não assinado eletronicamente.</div>
                          <div>Após a assinatura, este bloco exibirá o QR code e o código público de conferência.</div>
                        `}
                    </div>
                  </div>
                </div>
              </div>

              <div class="footer">
                <div class="footer-content">
                  <div class="footer-left">
                    <div class="footer-title">${safeBrandingDisplayName} • documento eletrônico de prontuário</div>
                    <div>${safeBrandingFooterLegal}</div>
                    <div>Documento confidencial. Uso restrito a profissionais autorizados e ao paciente.</div>
                  </div>
                  <div class="footer-right">
                    <div>
                      <div class="powered-title">Powered by Revahub</div>
                      <div class="powered-copy">© 2026 Revahub. Todos os direitos reservados.</div>
                    </div>
                    ${revahubMarkDataUrl ? `<div class="revahub-mark"><img src="${revahubMarkDataUrl}" alt="Revahub" /></div>` : ''}
                  </div>
                </div>
              </div>
            </div>
          `;

          return paginateDocumentBlocks({
            documentStyles,
            firstPagePrefix,
            continuationPagePrefix,
            textBlocks,
            buildFlowBlockHtml: (text, isContinuationPage) => `
              <div class="evolution-section ${isContinuationPage ? 'evolution-section-continuation' : 'evolution-section-primary'}">
                <div class="evolution-header">
                  <div class="evolution-title">${isContinuationPage ? 'Continuação do registro' : 'Registro da Evolução'}</div>
                </div>
                <div class="evolution-body">
                  ${!isContinuationPage && safeVitalSignsText
                    ? `<div class="evolution-vital-signs"><strong>Sinais vitais:</strong> ${safeVitalSignsText}</div>`
                    : ''}
                  <div class="evolution-text">${escapeHtml(text || 'Nenhuma evolução registrada')}</div>
                </div>
              </div>
            `,
            finalCompositeBlock,
          });
        }),
      );

      return pageGroups.flat();
    };

    const documentStyles = `
      <style>
          .capture-root {
            width: ${PRINT_CAPTURE_WIDTH_PX}px;
            background: #f3f6fb;
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          .capture-root,
          .capture-root * {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .capture-root {
            font-size: 10.5pt;
            line-height: 1.5;
            color: #1f2937;
            background: #f3f6fb;
          }

          .document-page {
            width: ${PRINT_CAPTURE_WIDTH_PX}px;
            min-height: 1123px;
            padding: 20px 24px;
            background: #f3f6fb;
            page-break-inside: avoid;
          }

          .document-page + .document-page {
            margin-top: 24px;
          }

          .document-container {
            max-width: 100%;
            margin: 0 auto;
            background: #ffffff;
            border: 1px solid #d9e2ef;
            border-radius: 18px;
            overflow: hidden;
            position: relative;
          }

          .page-accent {
            height: 8px;
            background: linear-gradient(90deg, #1f4f7f 0%, #2e5a88 45%, #78a7d6 100%);
          }

          .header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 16px;
            padding: 14px 20px 10px;
            background:
              linear-gradient(135deg, rgba(46, 90, 136, 0.06), transparent 58%),
              linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
            border-bottom: 1px solid #e2e8f0;
          }

          .header-left {
            display: flex;
            align-items: center;
            gap: 16px;
            min-width: 0;
            flex: 1;
          }

          .logo-container {
            width: 56px;
            height: 56px;
            flex: 0 0 56px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 12px;
            background: white;
            border: 1px solid #d7e2ee;
            overflow: hidden;
            box-shadow: inset 0 0 0 1px rgba(255,255,255,0.55);
          }

          .logo-container img {
            max-width: 46px;
            max-height: 46px;
            object-fit: contain;
          }

          .logo-fallback {
            width: 38px;
            height: 38px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #2e5a88;
            color: white;
            font-size: 12pt;
            font-weight: 700;
          }

          .brand-stack {
            min-width: 0;
          }

          .brand-kicker {
            font-size: 6.5pt;
            text-transform: uppercase;
            letter-spacing: 0.18em;
            color: #7a8ba3;
            margin-bottom: 2px;
            font-weight: 700;
          }

          .tenant-title {
            font-size: 10.8pt;
            line-height: 1.15;
            color: #1e293b;
            font-weight: 700;
            margin-bottom: 1px;
          }

          .tenant-subtitle {
            font-size: 7.7pt;
            color: #64748b;
            line-height: 1.2;
          }

          .document-summary {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 8px 20px 6px;
            border-bottom: 1px solid #edf2f7;
          }

          .document-summary-left {
            flex: 1;
            min-width: 0;
          }

          .section-heading-kicker {
            font-size: 6.3pt;
            text-transform: uppercase;
            letter-spacing: 0.16em;
            color: #7a8ba3;
            font-weight: 700;
            margin-bottom: 0;
          }

          .document-summary h1 {
            font-size: 10.6pt;
            line-height: 1.05;
            color: #0f2740;
            font-weight: 800;
          }

          .document-summary-meta {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            flex-wrap: wrap;
          }

          .document-summary-meta span,
          .continuation-banner-meta span {
            display: inline-flex;
            align-items: center;
            min-height: 20px;
            padding: 3px 9px;
            border-radius: 999px;
            background: #edf4fb;
            color: #24476a;
            font-size: 6.7pt;
            font-weight: 600;
            border: 1px solid #d4e1ee;
          }

          .continuation-banner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 9px 20px 8px;
            border-bottom: 1px solid #edf2f7;
            background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
          }

          .continuation-banner-left {
            flex: 1;
            min-width: 0;
          }

          .continuation-kicker {
            font-size: 6.1pt;
            text-transform: uppercase;
            letter-spacing: 0.14em;
            color: #7a8ba3;
            font-weight: 700;
            margin-bottom: 2px;
          }

          .continuation-patient {
            color: #17324d;
            font-size: 8.4pt;
            line-height: 1.15;
            font-weight: 700;
          }

          .continuation-banner-meta {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 6px;
            flex-wrap: wrap;
          }

          .status-pill {
            background: linear-gradient(180deg, #1f4f7f 0%, #2e5a88 100%) !important;
            color: #ffffff !important;
            border-color: #1f4f7f !important;
            box-shadow: 0 8px 18px rgba(31, 79, 127, 0.18);
          }

          .record-meta-band {
            display: grid;
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.4fr);
            gap: 12px;
            align-items: center;
            margin: 8px 20px 10px;
            padding: 10px 12px;
            border: 1px solid #dbe5f0;
            border-radius: 12px;
            background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
          }

          .record-meta-primary {
            min-width: 0;
            align-self: stretch;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding-right: 12px;
            border-right: 1px solid #e8eef5;
          }

          .record-meta-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px 10px;
            align-self: stretch;
          }

          .record-meta-item {
            min-width: 0;
            padding: 7px 9px 6px;
            border: 1px solid #e7edf5;
            border-radius: 10px;
            background: #fbfdff;
          }

          .record-meta-label {
            display: block;
            margin-bottom: 2px;
            color: #73859d;
            font-size: 6.5pt;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
          }

          .record-meta-name {
            color: #0f2740;
            font-size: 10.8pt;
            line-height: 1.15;
            font-weight: 700;
          }

          .record-meta-value {
            display: block;
            color: #1c334a;
            font-size: 7.7pt;
            line-height: 1.2;
            font-weight: 700;
            word-break: break-word;
          }

          .record-meta-subvalue {
            display: block;
            margin-top: 2px;
            color: #607286;
            font-size: 6.8pt;
            line-height: 1.2;
            word-break: break-word;
          }

          .record-meta-item-professional .record-meta-value {
            margin-bottom: 2px;
          }

          .evolution-section {
            background: #ffffff;
            border: 1px solid #dbe5f0;
            border-radius: 16px;
            margin: 0 20px 0;
            overflow: hidden;
          }

          .evolution-section + .evolution-section {
            margin-top: 10px;
          }

          .evolution-header {
            background:
              linear-gradient(135deg, #f5f9fe 0%, #eef5fc 100%);
            padding: 9px 14px;
            border-bottom: 1px solid #e3ebf4;
            display: flex;
            align-items: center;
            justify-content: flex-start;
          }

          .evolution-title {
            font-size: 9.6pt;
            font-weight: 700;
            color: #17324d;
          }

          .evolution-body {
            padding: 16px 18px;
            background:
              linear-gradient(180deg, rgba(250,252,255,0.85) 0%, rgba(255,255,255,0.95) 20%, #ffffff 100%);
          }

          .evolution-section-continuation .evolution-body {
            padding-top: 14px;
            padding-bottom: 14px;
          }

          .evolution-text {
            text-align: justify;
            white-space: pre-wrap;
            line-height: 1.78;
            font-size: 9.25pt;
            color: #33475c;
          }

          .evolution-vital-signs {
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e3ebf4;
            color: #33475c;
            font-size: 8.8pt;
            line-height: 1.5;
          }

          .document-final-block {
            page-break-inside: avoid;
          }

          .verification-section {
            margin-top: 14px;
            page-break-inside: avoid;
            padding: 0 20px 0;
          }

          .verification-panel {
            display: grid;
            grid-template-columns: 96px 1fr;
            gap: 16px;
            align-items: center;
            border: 1px solid #d6e1ed;
            border-radius: 14px;
            background: #f8fbff;
            padding: 14px 16px;
          }

          .verification-qr {
            width: 96px;
            height: 96px;
            border-radius: 10px;
            border: 1px solid #d7e2ee;
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .verification-qr img {
            width: 88px;
            height: 88px;
            object-fit: contain;
          }

          .verification-placeholder {
            width: 74px;
            height: 74px;
            border: 1px dashed #94a3b8;
            border-radius: 8px;
          }

          .verification-content {
            padding-top: 0;
          }

          .verification-title {
            font-size: 10.6pt;
            font-weight: 700;
            color: #17324d;
            margin-bottom: 6px;
          }

          .verification-copy {
            font-size: 8.8pt;
            color: #475569;
            line-height: 1.55;
          }

          .verification-copy strong {
            color: #1e293b;
          }

          .verification-link {
            color: #1f4f7f;
            word-break: break-all;
            font-size: 8.3pt;
          }

          .footer {
            margin: 14px 20px 20px;
            padding: 12px 14px;
            border-top: 1px solid #e3ebf4;
            background: #f8fbff;
            border-radius: 14px;
          }

          .footer-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            font-size: 7.4pt;
            color: #64748b;
            gap: 18px;
          }

          .footer-left {
            line-height: 1.5;
            flex: 1;
          }

          .footer-title {
            color: #17324d;
            font-size: 8.2pt;
            font-weight: 700;
            margin-bottom: 4px;
          }

          .footer-right {
            text-align: right;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            min-width: 170px;
          }

          .revahub-mark {
            width: 28px;
            height: 28px;
            flex: 0 0 28px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .revahub-mark img {
            max-width: 28px;
            max-height: 28px;
            object-fit: contain;
          }

          .powered-title {
            color: #1f4f7f;
            font-size: 8pt;
            font-weight: 700;
            line-height: 1.2;
          }

          .powered-copy {
            color: #94a3b8;
            font-size: 6.5pt;
            line-height: 1.2;
          }
      </style>
    `;

    const documentPages = await buildDocumentPages(documentStyles);
    const pdf = await renderDocumentPagesToPdf(documentPages, documentStyles);
    pdf.setProperties({
      title:
        sortedProntuarios.length > 1
          ? `Evoluções - ${documentTitlePatient}`
          : `Evolução - ${documentTitlePatient}`,
      subject: 'Documento eletrônico de prontuário',
      author: safeBrandingDisplayName,
      creator: 'Revahub',
    });
    pdf.autoPrint();

    const pdfBytes = new Uint8Array(pdf.output('arraybuffer'));

    if (generatedFileToken && activeClientId) {
      const sourceDocuments = sortedProntuarios.flatMap((item) => {
          const itemSignature = item.signature;
          if (!itemSignature?.verificationToken) {
            return [];
          }

          return [{
            recordId: item.id,
            patientId: item.patientId,
            patientName: item.patientFullName || null,
            patientMedicalRecordNumber: item.medicalRecordNumber || null,
            documentTitle: item.recordTitle || 'Evolução Clínica',
            verificationToken: itemSignature.verificationToken,
            documentHash: itemSignature.documentHash,
            signedAt: itemSignature.signedAt,
            professionalName: itemSignature.signedByName || item.author?.name || null,
            professionalRole: itemSignature.signedByRole || item.author?.professionalTitle || null,
            professionalRegistration:
              itemSignature.signedByRegistration || item.author?.councilNumber || null,
            status: item.status === 'invalidated' ? 'invalidated' : 'valid',
          }];
        });

      const uniquePatients = Array.from(
        new Set(sourceDocuments.map((item) => item.patientName).filter((item): item is string => Boolean(item))),
      );
      const uniqueProfessionalNames = Array.from(
        new Set(sourceDocuments.map((item) => item.professionalName).filter((item): item is string => Boolean(item))),
      );
      const uniqueProfessionalRoles = Array.from(
        new Set(sourceDocuments.map((item) => item.professionalRole).filter((item): item is string => Boolean(item))),
      );
      const uniqueProfessionalRegistrations = Array.from(
        new Set(
          sourceDocuments
            .map((item) => item.professionalRegistration)
            .filter((item): item is string => Boolean(item)),
        ),
      );
      const isBundle = sourceDocuments.length > 1;
      const primaryDocument = sourceDocuments[0] || null;
      const bundleDocumentHash = isBundle
        ? await hashVerificationPayload({
            documentType: 'medical_record_bundle',
            sourceDocuments: sourceDocuments.map((item) => ({
              recordId: item.recordId,
              verificationToken: item.verificationToken,
              documentHash: item.documentHash,
              status: item.status,
            })),
          })
        : primaryDocument?.documentHash || '';

      await DocumentVerificationService.registerGeneratedDocumentFileVerification({
        clientId: activeClientId,
        tenantSlug,
        tenantName: selectedClient?.clientName || branding.displayName || branding.legalName,
        documentType: isBundle ? 'medical_record_bundle' : 'medical_record',
        documentId: isBundle ? null : primaryDocument?.recordId || null,
        documentTitle: isBundle
          ? 'Lote de evoluções clínicas'
          : primaryDocument?.documentTitle || 'Evolução Clínica',
        patientId:
          uniquePatients.length === 1 && !isBundle ? primaryDocument?.patientId || null : null,
        patientName:
          uniquePatients.length === 0
            ? null
            : uniquePatients.length === 1
              ? uniquePatients[0]
              : 'Múltiplos pacientes',
        patientMedicalRecordNumber:
          sourceDocuments.length === 1 ? primaryDocument?.patientMedicalRecordNumber || null : null,
        professionalName:
          uniqueProfessionalNames.length === 0
            ? null
            : uniqueProfessionalNames.length === 1
              ? uniqueProfessionalNames[0]
              : 'Múltiplos profissionais',
        professionalRole:
          uniqueProfessionalRoles.length === 1 ? uniqueProfessionalRoles[0] : null,
        professionalRegistration:
          uniqueProfessionalRegistrations.length === 1 ? uniqueProfessionalRegistrations[0] : null,
        signedAt:
          sourceDocuments
            .map((item) => item.signedAt)
            .filter(Boolean)
            .sort()
            .slice(-1)[0] || generatedAtIso,
        status: sourceDocuments.some((item) => item.status === 'invalidated') ? 'invalidated' : 'valid',
        documentHash: bundleDocumentHash,
        templateVersion: primaryDocument ? 1 : 1,
        snapshot: {
          sourceDocumentTokens: sourceDocuments.map((item) => item.verificationToken),
          sourceDocuments,
          printedRecordCount: sourceDocuments.length,
          printedAt: generatedAtIso,
        },
        fileToken: generatedFileToken,
        sourceDocumentToken: isBundle ? null : primaryDocument?.verificationToken || null,
        fileHash: await hashBinaryContent(pdfBytes),
        generatedAt: generatedAtIso,
        generatedByUserId: user?.userId || null,
        generatedByName: user?.userDisplayName || null,
        generationChannel: 'print',
        fileName: `${
          isBundle ? 'evolucoes' : 'evolucao'
        }-${sanitizeFileNamePart(documentTitlePatient || 'paciente')}-${generatedAtIso.slice(0, 10)}.pdf`,
        fileSizeBytes: pdfBytes.byteLength,
      });
    }

    openPdfBytesForPrint(pdfBytes);
  };

  const printEvolution = async (prontuario: ProntuarioData) => {
    await printEvolutions([prontuario]);
  };

  // Buscar última evolução do paciente no Supabase
  const fetchLastEvolution = async (patientId: string) => {
    if (!selectedClient?.clientId) {
      setShowLastEvolution(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("medical_records")
        .select("recordDescription, recordDate, recordType, recordAttachments")
        .eq("clientId", selectedClient.clientId)
        .eq("patientId", patientId)
        .eq("recordType", "progress")
        .order("recordDate", { ascending: false });

      if (error) {
        console.error("Erro ao buscar última evolução no Supabase:", error);
        setShowLastEvolution(false);
        return;
      }

      const lastValidEvolution = (data || []).find(
        (record) =>
          !isMedicalRecordInvalidated(record.recordType, record.recordAttachments) &&
          !isClinicalHistoryRecord(record.recordAttachments),
      );

      if (lastValidEvolution) {
        const lastEvolutionText = lastValidEvolution.recordDescription || '';
        const cleanText = extractTextFromHTML(lastEvolutionText);

        if (cleanText && cleanText !== 'Não informado') {
          setLastEvolution(cleanText);
          setShowLastEvolution(true);
        } else {
          setShowLastEvolution(false);
        }
      } else {
        setShowLastEvolution(false);
      }
    } catch (error) {
      console.error('Erro ao buscar última evolução:', error);
      setShowLastEvolution(false);
    }
  };


  // Lidar com seleção de paciente
  const handlePatientSelection = async (patientId: string) => {
    const selectedPatient = patients.find((p) => p.id === patientId);
    const blockReason = selectedPatient ? getPatientNewCareBlockReason(selectedPatient) : null;
    if (selectedPatient && blockReason) {
      toast.error(
        blockReason === 'clinical_inactive'
          ? 'Paciente está inativo assistencialmente (alta/parado/óbito) e não pode receber novas evoluções.'
          : 'Paciente inativado/arquivado no cadastro não pode receber novas evoluções.'
      );
      return;
    }

    if (patientId !== newProntuarioForm.selectedPatientId) {
      setNewEvolutionVitalSignsDraft(
        createEmptyMedicalRecordVitalSignsDraft(newProntuarioForm.datetime),
      );
    }

    setNewProntuarioForm(prev => ({
      ...prev,
      selectedPatientId: patientId,
      selectedAppointmentId: '' // Reset appointment selection
    }));
    setAppointmentScope('mine');
    setShowAppointmentPicker(false);
    setShowLastEvolutionDetails(false);

    if (patientId) {
      fetchLastEvolution(patientId);
      await loadPatientAppointments(patientId);
    } else {
      setShowLastEvolution(false);
      setLastEvolution('');
      setPatientAppointments([]);
      setCategorizedAppointments({ available: [], linked: [], currentLink: null });
    }
  };

  const closeNewEvolutionModal = () => {
    setShowLastEvolutionDetails(false);
    setShowAppointmentPicker(false);
    setNewEvolutionVitalSignsDraft(createEmptyMedicalRecordVitalSignsDraft());
    setShowNewProntuarioModal(false);
  };

  // 🆕 Carregar agendamentos do paciente selecionado (versão categorizada)
  const loadPatientAppointments = async (
    patientId: string,
    currentEvolutionId?: string,
    preferredAppointmentId?: string,
  ) => {
    try {
      setLoadingAppointments(true);

      // Usar novo método categorizado
      const categorized = await IntegrationService.getCategorizedAppointmentsForLinking(
        patientId,
        currentEvolutionId,
        preferredAppointmentId,
      );

      setCategorizedAppointments(categorized);

      // Manter compatibilidade com código legado (concatenar todos os agendamentos)
      const allAppointments = [
        ...(categorized.currentLink ? [categorized.currentLink] : []),
        ...categorized.available,
        ...categorized.linked
      ];
      setPatientAppointments(allAppointments);

      const hasAppointmentFromCurrentUser = allAppointments.some(isAppointmentFromCurrentUser);
      if (allAppointments.length > 0 && !hasAppointmentFromCurrentUser) {
        setAppointmentScope('all');
      }

      console.log(`✅ Agendamentos categorizados carregados:`);
      console.log(`   - Disponíveis: ${categorized.available.length}`);
      console.log(`   - Já vinculados: ${categorized.linked.length}`);
      console.log(`   - Vínculo atual: ${categorized.currentLink ? 'Sim' : 'Não'}`);
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      setPatientAppointments([]);
      setCategorizedAppointments({ available: [], linked: [], currentLink: null });
    } finally {
      setLoadingAppointments(false);
    }
  };

  useEffect(() => {
    if (!showNewProntuarioModal) {
      return;
    }

    const patientId = newProntuarioForm.selectedPatientId;
    if (!patientId) {
      setShowLastEvolution(false);
      setLastEvolution('');
      setPatientAppointments([]);
      setCategorizedAppointments({ available: [], linked: [], currentLink: null });
      return;
    }

    fetchLastEvolution(patientId);
    void loadPatientAppointments(
      patientId,
      undefined,
      newProntuarioForm.selectedAppointmentId || undefined,
    );
  }, [
    showNewProntuarioModal,
    newProntuarioForm.selectedPatientId,
  ]);

  // Copiar última evolução
  const copyLastEvolution = () => {
    setNewProntuarioForm(prev => ({
      ...prev,
      evolution: lastEvolution
    }));
    toast.success('Última evolução copiada!');
  };

  const copyClinicalHistory = (clinicalHistory: string) => {
    setNewProntuarioForm((current) => ({
      ...current,
      evolution: current.evolution.trim()
        ? `${current.evolution.trim()}\n\nHistórico clínico de referência:\n${clinicalHistory}`
        : clinicalHistory,
    }));
    toast.success('Registro clínico copiado para a evolução.');
  };

  const openNewAssessmentForPatient = (patientId: string) => {
    if (!selectedClient) return;

    const isPhysiotherapistRoute = location.pathname.includes('/fisioterapeuta/');
    const assessmentPath = isPhysiotherapistRoute
      ? '/fisioterapeuta/avaliacoes'
      : '/admin/assessments-v2';
    const tenantPath = buildTenantPath(assessmentPath, selectedClient.clientSlug || null);
    const assessmentUrl = new URL(tenantPath, window.location.origin);
    assessmentUrl.searchParams.set('patientId', patientId);
    assessmentUrl.searchParams.set('action', 'new');
    assessmentUrl.searchParams.set('source', 'evolution');

    const assessmentWindow = window.open(assessmentUrl.toString(), '_blank');
    if (!assessmentWindow) {
      toast.error('O navegador bloqueou a abertura da avaliação. Permita pop-ups para continuar.');
    } else {
      assessmentWindow.opener = window;
    }
  };

  useEffect(() => {
    const handleAssessmentCompletion = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin || !event.data || typeof event.data !== 'object') return;

      const message = event.data as {
        type?: string;
        clientId?: string;
        patientId?: string;
      };
      if (message.type !== 'revahub:assessment-completed') return;
      if (message.clientId !== selectedClient?.clientId) return;
      if (message.patientId !== newProntuarioForm.selectedPatientId) return;

      setTimelineRefreshVersion((current) => current + 1);
      toast.success('Avaliação concluída e adicionada à timeline do paciente.');
      window.focus();
    };

    window.addEventListener('message', handleAssessmentCompletion);
    return () => window.removeEventListener('message', handleAssessmentCompletion);
  }, [newProntuarioForm.selectedPatientId, selectedClient?.clientId]);

  // Salvar novo prontuário no Supabase
  const saveNewProntuario = async () => {
    if (!newProntuarioForm.selectedPatientId || !newProntuarioForm.datetime || !newProntuarioForm.evolution.trim()) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (!user || !user.userId || !selectedClient || !selectedClient.clientId) {
      toast.error("Usuário ou cliente não autenticado/selecionado.");
      return;
    }

    const parsedVitalSigns = parseMedicalRecordVitalSignsDraft(
      newEvolutionVitalSignsDraft,
      newProntuarioForm.datetime,
    );
    if (parsedVitalSigns.error) {
      toast.error(parsedVitalSigns.error);
      return;
    }

    try {
      setIsSubmittingProntuario(true);

      // Buscar dados do paciente selecionado (já vem como UnifiedPatient)
      const selectedPatient = patients.find(p => p.id === newProntuarioForm.selectedPatientId);
      
      if (!selectedPatient) {
        toast.error("Paciente selecionado não encontrado");
        return;
      }

      const createBlockReason = getPatientNewCareBlockReason(selectedPatient);
      if (createBlockReason) {
        toast.error(
          createBlockReason === 'clinical_inactive'
            ? 'Paciente está inativo assistencialmente (alta/parado/óbito) e não pode receber novas evoluções.'
            : 'Paciente inativado/arquivado no cadastro não pode receber novas evoluções.'
        );
        return;
      }

      const currentAuthorDirectory = await fetchMedicalRecordAuthors(
        [user.userId],
        selectedClient.clientId,
      );
      const persistedAuthor = currentAuthorDirectory.get(user.userId) ?? null;

      // Preparar dados do novo registro médico para o Supabase
      const newMedicalRecordData: Partial<SupabaseMedicalRecordRow> = {
        clientId: selectedClient.clientId,
        patientId: newProntuarioForm.selectedPatientId,
        doctorId: user.userId,
        recordType: 'progress',
        recordDate: new Date(newProntuarioForm.datetime).toISOString(),
        recordTitle: 'Evolução',
        recordDescription: newProntuarioForm.evolution,
        recordAttachments: {
          authorSnapshot:
            persistedAuthor ||
            buildMedicalRecordAuthorSnapshot({
              userId: user.userId,
              name: user.userDisplayName || null,
              role: selectedClient.userRole || user.userRole || null,
            }),
          ...(parsedVitalSigns.value ? { vitalSigns: parsedVitalSigns.value } : {}),
        },
      };

      // Salvar no Supabase
      const { data: createdRecord, error: insertError } = await supabase
        .from("medical_records")
        .insert(newMedicalRecordData)
        .select()
        .single();

      if (insertError) {
        console.error("Erro ao salvar prontuário no Supabase:", insertError);
        toast.error("Erro ao salvar prontuário");
        setIsSubmittingProntuario(false);
        return;
      }
      
      const evolutionId = createdRecord?.recordId || (createdRecord as any)?.id;
      if (!evolutionId) {
        throw new Error("Não foi possível identificar o ID da evolução criada.");
      }

      // 🆕 VINCULAÇÃO MANUAL: Usar agendamento selecionado pelo usuário
      try {
        const selectedAppointmentId = newProntuarioForm.selectedAppointmentId;

        if (selectedAppointmentId && selectedAppointmentId !== 'none') {
          console.log('🔗 Vinculando evolução e disparando conclusão do agendamento:', selectedAppointmentId);

          // Primeiro, vincula a evolução (recordId) ao agendamento.
          await IntegrationService.linkEvolutionToAppointment(evolutionId, selectedAppointmentId);

          // Em seguida, chama o serviço principal que contém TODA a lógica de negócio:
          // - Debita do pacote OU gera cobrança avulsa
          // - Marca o agendamento como 'completed' e 'billed'
          await AppointmentStatusService.completeAppointment(selectedAppointmentId, user.userId, evolutionId);

          console.log('✅ Evolução vinculada e agendamento processado para faturamento.');
          toast.success('Evolução registrada e agendamento concluído com sucesso!');

        } else {
          console.log('ℹ️ Evolução salva sem vínculo com agendamento (escolha do usuário)');
          toast.success('Evolução registrada (sem vínculo com agendamento)');
        }
      } catch (error) {
        console.error('❌ Erro ao vincular evolução:', error);
        toast.warning('Evolução salva, mas não foi possível vincular ao agendamento');
      }

      await fetchAndSetProntuarios();
      
      
      // Reset form e fechar modal
      setNewProntuarioForm({
        selectedPatientId: '',
        datetime: (() => {
          const now = new Date();
          const brasiliaOffset = -3 * 60; // UTC-3 (horário de Brasília)
          const brasiliaTime = new Date(now.getTime() + (brasiliaOffset * 60 * 1000));
          return brasiliaTime.toISOString().slice(0, 16);
        })(),
        evolution: '',
        selectedAppointmentId: '' // 🆕 Reset appointment selection
      });
      setNewEvolutionVitalSignsDraft(createEmptyMedicalRecordVitalSignsDraft());
      setShowLastEvolution(false);
      setLastEvolution('');
      setShowLastEvolutionDetails(false);
      setPatientAppointments([]); // 🆕 Limpar lista de agendamentos
      setShowNewProntuarioModal(false);

      console.log('✅ Prontuário salvo, aguardando sincronização automática...');

    } catch (error) {
      console.error('Erro ao salvar prontuário:', error);
      toast.error('Erro ao salvar prontuário');
    } finally {
      setIsSubmittingProntuario(false);
    }
  };

  const processProntuarioData = useCallback(async (
    record: SupabaseMedicalRecordRow & {
      patients?: { patientFullName?: string; patientId?: string; patientCpf?: string | null };
    },
    authorDirectory: Map<string, MedicalRecordAuthorInfo>,
  ): Promise<ProntuarioData> => {
    const patientFullName = record.patients?.patientFullName || (record as any).patientFullName;
    const medicalRecordNumber =
      (record as any).medicalRecordNumber ||
      record.patients?.patientCpf ||
      undefined;
    const attachments = getMedicalRecordAttachmentsObject(record.recordAttachments);
    const snapshotAuthor = getMedicalRecordAuthorFromAttachments(record.recordAttachments);
    const signature = getMedicalRecordSignatureMetadata(record.recordAttachments);
    const fallbackAuthor = record.doctorId ? authorDirectory.get(record.doctorId) ?? null : null;
    const author = mergeMedicalRecordAuthorInfo(snapshotAuthor, fallbackAuthor);

    const prontuario: ProntuarioData = {
        id: record.recordId,
        patientId: record.patientId,
        doctorId: record.doctorId,
        recordDate: record.recordDate,
        recordDescription: record.recordDescription || 'Não informado',
        recordCreatedAt: record.recordCreatedAt,
        recordUpdatedAt: record.recordUpdatedAt,
        patientFullName: patientFullName,
        medicalRecordNumber: medicalRecordNumber,
        status: getMedicalRecordLifecycleStatus(record.recordType, record.recordAttachments),
        agendamentoId: attachments.agendamentoId,
        invalidation_info: attachments.invalidation_info,
        invalidation_history: attachments.invalidation_history,
        signature,
        author,
        vitalSigns: getMedicalRecordVitalSigns(record.recordAttachments),
    };

    return prontuario;
  }, []);

  const fetchAndSetProntuarios = useCallback(async (): Promise<ProntuarioData[]> => {
    if (!selectedClient?.clientId) {
      setConnectionError("Cliente não identificado para carregar evoluções.");
      setLoading(false);
      setIsRefreshing(false);
      return [];
    }

    try {
      const { data, error } = await supabase
        .from("medical_records")
        .select(`
          *,
          patients(patientFullName, patientId, patientCpf)
        `)
        .eq("clientId", selectedClient.clientId)
        .order("recordDate", { ascending: false });

      if (error) {
        console.error("Erro ao buscar evoluções do Supabase:", error);
        setConnectionError(`Erro ao carregar evoluções: ${error.message}`);
        toast.error("Erro ao carregar evoluções");
        setLoading(false);
        setIsRefreshing(false);
        return [];
      }

      const evolutionRecords = (data || []).filter(
        (record: SupabaseMedicalRecordRow) => !isClinicalHistoryRecord(record.recordAttachments),
      );

      const authorDirectory = await fetchMedicalRecordAuthors(
        evolutionRecords
          .filter((record: SupabaseMedicalRecordRow) => !getMedicalRecordAuthorFromAttachments(record.recordAttachments))
          .map((record: SupabaseMedicalRecordRow) => record.doctorId),
        selectedClient.clientId,
      );

      const prontuariosData = await Promise.all(
        evolutionRecords.map((record) => processProntuarioData(record, authorDirectory)),
      );
      setAllProntuarios(prontuariosData);
      setTotalItems(prontuariosData.length);
      previousCountRef.current = prontuariosData.length;
      setConnectionError("");

      return prontuariosData;
    } catch (error: any) {
      console.error("Erro ao processar dados de evoluções:", error);
      setConnectionError(`Erro ao processar dados: ${error.message}`);
      toast.error("Erro ao processar dados");
      return [];
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [processProntuarioData, selectedClient?.clientId]);

  const setupRealtimeListener = useCallback(() => {
    try {
      console.log("🔍 Configurando listener em tempo real...");
      setConnectionError("");

      if (!selectedClient?.clientId) {
        console.warn("ClientId não disponível, não configurando listener de prontuários.");
        setLoading(false);
        return () => {}; // Retorna uma função vazia para unsubscribe
      }

      // Carrega os dados iniciais
      fetchAndSetProntuarios();

      // Configura o listener em tempo real para o Supabase
      const channel = supabase
        .channel("medical_records_changes")
        .on<SupabaseMedicalRecordRow>(
          "postgres_changes",
          { 
            event: "*", 
            schema: "public", 
            table: "medical_records", 
            filter: `clientId=eq.${selectedClient.clientId}`
          },
          async (payload) => {
            console.log("📋 Atualização em tempo real recebida:", payload.eventType);
            const previousCount = previousCountRef.current;
            const updatedProntuarios = await fetchAndSetProntuarios();
            const currentCount = updatedProntuarios.length;

            if (previousCount > 0 && currentCount > previousCount) {
              const newItems = currentCount - previousCount;
              setNewItemsCount(newItems);
              setShowUpdateNotification(true);
              setTimeout(() => setShowUpdateNotification(false), 5000);
            }
          }
        )
        .subscribe();

      unsubscribeRef.current = () => {
        console.log("🔌 Desconectando listener de prontuários...");
        supabase.removeChannel(channel);
      };
      return unsubscribeRef.current;
    } catch (error: any) {
      console.error('❌ Erro ao configurar listener:', error);
      setConnectionError(`Erro ao configurar listener: ${error.message}`);
      toast.error('Erro ao configurar sincronização automática');
      setLoading(false);
      setIsRefreshing(false);
      return () => {};
    }
  }, [fetchAndSetProntuarios, selectedClient?.clientId]);

  const refreshData = useCallback(async () => {
    console.log('🔄 Solicitando atualização manual dos dados...');
    setIsRefreshing(true);
    toast.info('Atualizando dados...');
    await fetchAndSetProntuarios();
  }, [fetchAndSetProntuarios]);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = setupRealtimeListener();

    return () => {
      if (unsubscribeRef.current) {
        console.log('🔌 Desconectando listener de prontuários...');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [setupRealtimeListener]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const patientId = params.get('patientId');
    setMedicalRecordView(params.get('view') === 'timeline' ? 'timeline' : 'evolutions');
    if (patientId) {
      setFilterPatient(patientId);
      window.history.replaceState({}, document.title);
      return;
    }

    const state = location.state as any;
    if (state?.preSelectedPatient && state?.preSelectedAppointment) {
      console.log('📋 Navegação com dados pre-selecionados:', state);

      setNewProntuarioForm(prev => ({
        ...prev,
        selectedPatientId: state.preSelectedPatient,
        selectedAppointmentId: state.preSelectedAppointment
      }));

      setFilterPatient(state.preSelectedPatient);

      setTimeout(() => {
        setShowNewProntuarioModal(true);
        toast.success('Paciente e agendamento selecionados automaticamente');
      }, 500);

      window.history.replaceState({}, document.title);
    }
  }, [location.search, location.state]);

  // Carregar lista de pacientes para filtros
  useEffect(() => {
    const loadPatients = async () => {
      try {
        if (!selectedClient?.clientId) {
          setPatients([]);
          return;
        }

        console.log("👥 Carregando lista de pacientes...");
        
        const unifiedPatients = await PatientService.getAllPatients(selectedClient.clientId);
        
        setPatients(unifiedPatients);

        console.log("✅ Total de pacientes carregados:", unifiedPatients.length);
        console.log("✅ Pacientes ativos:", unifiedPatients.filter(p => p.financialProfile?.active).length);
        console.log("✅ Pacientes inativos:", unifiedPatients.filter(p => !p.financialProfile?.active).length);
      } catch (error) {
        console.error("❌ Erro ao carregar pacientes:", error);
        toast.error("Erro ao carregar lista de pacientes");
      }
    };

    loadPatients();
  }, [selectedClient?.clientId]);

  // Função para invalidar evolução
  const handleInvalidateEvolution = useCallback(async (evolutionId: string, justification: string) => {
    if (!user) {
      toast.error("Usuário não autenticado");
      return false;
    }
    if (!selectedClient?.clientId) {
      toast.error("Tenant não identificado");
      return false;
    }

    try {
      const { data: currentRecord, error: fetchError } = await supabase
        .from("medical_records")
        .select("recordAttachments, recordType")
        .eq("recordId", evolutionId)
        .eq("clientId", selectedClient.clientId)
        .single();

      if (fetchError) {
        console.error("Erro ao buscar registro para invalidação:", fetchError);
        toast.error("Erro ao buscar registro para invalidação");
        return false;
      }

      const invalidationData: InvalidationInfo = {
        timestamp: new Date().toISOString(),
        user_uid: user.userId,
        user_name: user.userDisplayName || "Admin",
        user_email: user.userEmail || "",
        justification: justification,
      };

      if (isMedicalRecordInvalidated(currentRecord.recordType, currentRecord.recordAttachments)) {
        toast.info("Esta evolução já está invalidada");
        return false;
      }

      const { data: previewData, error: previewError } = await supabase.rpc(
        "rpc_preview_medical_record_invalidation_origin",
        {
          p_record_id: evolutionId,
          p_client_id: selectedClient.clientId,
        },
      );

      if (previewError) {
        console.error("Erro ao executar pré-validação da invalidação:", previewError);
        toast.error("Erro ao preparar invalidação");
        return false;
      }

      const preview = (previewData || {}) as InvalidationPreviewResult;
      const conflictCandidates = Array.isArray(preview.conflictCandidates)
        ? preview.conflictCandidates
        : [];
      const recommendedAppointmentId = preview.candidateByEvolutionId?.appointmentId || null;
      const candidateByAttachmentId = preview.candidateByAttachmentId?.appointmentId || null;
      const candidateByEvolutionId = preview.candidateByEvolutionId?.appointmentId || null;

      let proceedWithoutAppointment = false;
      let selectedAppointmentId: string | null = null;

      if (preview.resolutionStatus === 'not_found') {
        const shouldContinue = window.confirm(
          "Não foi encontrado agendamento de origem para esta evolução.\n\nDeseja continuar com a invalidação sem alterar nenhum agendamento?",
        );
        if (!shouldContinue) {
          toast.info("Invalidação cancelada pelo usuário.");
          return false;
        }
        proceedWithoutAppointment = true;
      } else if (preview.resolutionStatus === 'conflict') {
        const selected = await openConflictResolutionDialog(
          conflictCandidates,
          recommendedAppointmentId,
        );
        if (!selected) {
          toast.info("Invalidação cancelada pelo usuário.");
          return false;
        }
        selectedAppointmentId = selected;
      } else if (preview.resolutionStatus === 'unique') {
        selectedAppointmentId = candidateByEvolutionId || candidateByAttachmentId;
      } else {
        console.error("Status de resolução inválido na pré-validação:", preview);
        toast.error("Não foi possível determinar a origem do agendamento");
        return false;
      }

      const unlinkNote =
        preview.resolutionStatus === 'not_found'
          ? 'Invalidação confirmada sem agendamento de origem localizado.'
          : 'Vínculo removido durante invalidação da evolução.';

      const { data: confirmData, error: confirmError } = await supabase.rpc(
        "rpc_confirm_medical_record_invalidation_atomic",
        {
          p_record_id: evolutionId,
          p_client_id: selectedClient.clientId,
          p_user_uid: user.userId,
          p_user_name: user.userDisplayName || "Admin",
          p_user_email: user.userEmail || "",
          p_justification: justification,
          p_proceed_without_appointment: proceedWithoutAppointment,
          p_selected_appointment_id: selectedAppointmentId,
          p_unlink_observation_note: unlinkNote,
        },
      );

      if (confirmError) {
        console.error("Erro ao confirmar invalidação atômica no Supabase:", confirmError);
        toast.error(`Erro ao invalidar evolução: ${confirmError.message}`);
        return false;
      }

      const confirmResult = (confirmData || {}) as InvalidationAtomicConfirmResult;
      const updatedAttachments = confirmResult.updatedRecordAttachments ||
        buildInvalidatedMedicalRecordAttachments(currentRecord.recordAttachments, invalidationData, currentRecord.recordType);

      applyInvalidationToLocalState(
        evolutionId,
        invalidationData,
        updatedAttachments,
        confirmResult.usedAppointmentId || null,
      );

      toast.success("Evolução invalidada com sucesso");
      if (preview.resolutionStatus === 'not_found') {
        toast.info("Invalidação concluída sem atualização de agendamento.");
      }

      return true;
    } catch (error) {
      console.error("Erro ao invalidar evolução:", error);
      toast.error("Erro ao invalidar evolução");
      return false;
    }
  }, [applyInvalidationToLocalState, openConflictResolutionDialog, selectedClient?.clientId, user]);

  const filteredProntuarios = useMemo(() => {
    const filtered = allProntuarios
      .filter(prontuario => {
        const normalizedSearch = normalizeSearchText(searchTerm);
        const patientName = prontuario.patientFullName || '';
        const professionalName = normalizeSearchText(prontuario.author?.name);
        const evolution = normalizeSearchText(extractTextFromHTML(prontuario.recordDescription || ''));

        const matchesSearch = (
          matchesPatientSearch(
            searchTerm,
            patientName,
            [prontuario.medicalRecordNumber],
          ) ||
          professionalName.includes(normalizedSearch) ||
          evolution.includes(normalizedSearch)
        );

        const matchesPatient = filterPatient === 'all' || prontuario.patientId === filterPatient;
        const matchesProfessional =
          filterProfessional === 'all' || (prontuario.author?.name || '') === filterProfessional;

        const matchesStatus =
          statusFilter === 'all' ? true :
          statusFilter === 'active' ? prontuario.status === 'active' :
          prontuario.status === 'invalidated';

        let matchesDate = true;
        if (dateFilter) {
          const prontuarioDate = new Date(prontuario.recordDate).toISOString().split('T')[0];
          matchesDate = prontuarioDate === dateFilter;
        }

        return matchesSearch && matchesPatient && matchesProfessional && matchesDate && matchesStatus;
      })
      .sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
          case 'paciente':
            aValue = a.patientFullName?.toLowerCase() || '';
            bValue = b.patientFullName?.toLowerCase() || '';
            break;
          case 'prontuario':
            aValue = a.medicalRecordNumber || '';
            bValue = b.medicalRecordNumber || '';
            break;
          case 'data':
            aValue = new Date(a.recordDate || 0).getTime();
            bValue = new Date(b.recordDate || 0).getTime();
            break;
          case 'status':
            aValue = a.status || 'active';
            bValue = b.status || 'active';
            break;
          case 'created':
            aValue = new Date(a.recordCreatedAt || 0).getTime();
            bValue = new Date(b.recordCreatedAt || 0).getTime();
            break;
          default:
            aValue = new Date(a.recordDate || 0).getTime();
            bValue = new Date(b.recordDate || 0).getTime();
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue, 'pt-BR')
            : bValue.localeCompare(aValue, 'pt-BR');
        }

        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });

    setTotalItems(filtered.length);
    return filtered;
  }, [allProntuarios, searchTerm, filterPatient, filterProfessional, dateFilter, statusFilter, sortColumn, sortDirection]);

  const paginatedProntuarios = useMemo(() => {
    if (pageSize === 'all') {
      return filteredProntuarios;
    }
    const startIndex = (currentPage - 1) * (pageSize as number);
    const endIndex = startIndex + (pageSize as number);
    return filteredProntuarios.slice(startIndex, endIndex);
  }, [filteredProntuarios, currentPage, pageSize]);

  const canUseBatchPrint = filterPatient !== 'all';
  const selectedBatchPrintProntuarios = useMemo(() => {
    if (selectedBatchPrintIds.length === 0) {
      return [];
    }

    const selectedIds = new Set(selectedBatchPrintIds);
    return paginatedProntuarios.filter((prontuario) => selectedIds.has(prontuario.id));
  }, [paginatedProntuarios, selectedBatchPrintIds]);

  const allVisibleProntuariosSelected =
    paginatedProntuarios.length > 0 &&
    paginatedProntuarios.every((prontuario) => selectedBatchPrintIds.includes(prontuario.id));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterPatient, dateFilter, statusFilter]);

  useEffect(() => {
    setSelectedBatchPrintIds([]);

    if (filterPatient === 'all') {
      setShowBatchPrintSelection(false);
    }
  }, [filterPatient, searchTerm, dateFilter, statusFilter, currentPage, pageSize, sortColumn, sortDirection]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageSizeChange = (size: number | 'all') => {
    setPageSize(size);
    localStorage.setItem(storageKey, size.toString());
    setCurrentPage(1);
  };

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalItems / (pageSize as number));
  const hasNextPage = pageSize !== 'all' && currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  useEffect(() => {
    if (pageSize === 'all') {
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(Math.max(totalPages, 1));
    }
  }, [currentPage, pageSize, totalPages]);

  const openVerificationPage = useCallback((verificationToken: string) => {
    window.open(buildVerificationUrl(verificationToken), '_blank', 'noopener,noreferrer');
  }, []);

  const handleSignEvolution = useCallback(async (prontuario: ProntuarioData) => {
    if (!user?.userId || !selectedClient?.clientId) {
      toast.error('Usuário ou tenant não identificado para assinar a evolução.');
      return;
    }

    if (prontuario.status === 'invalidated') {
      toast.error('Evoluções invalidadas não podem ser assinadas.');
      return;
    }

    if (prontuario.signature?.verificationToken) {
      toast.info('Esta evolução já está assinada eletronicamente.');
      return;
    }

    const shouldSign = window.confirm(
      'Assinar eletronicamente esta evolução bloqueará edições futuras e liberará o QR code de autenticidade.\n\nDeseja continuar?',
    );

    if (!shouldSign) {
      return;
    }

    try {
      setSigningEvolutionId(prontuario.id);

      const authorDirectory = await fetchMedicalRecordAuthors([user.userId], selectedClient.clientId);
      const signer =
        authorDirectory.get(user.userId) ??
        buildMedicalRecordAuthorSnapshot({
          userId: user.userId,
          name: user.userDisplayName || null,
          role: selectedClient.userRole || user.userRole || null,
        });

      if (requiresProfessionalRegistration(signer.role) && !signer.councilNumber) {
        toast.error('Não foi encontrado um registro profissional para o assinante atual.');
        return;
      }

      const { data: currentRecord, error } = await supabase
        .from('medical_records')
        .select('recordAttachments, recordDate, recordDescription, recordTitle')
        .eq('recordId', prontuario.id)
        .eq('clientId', selectedClient.clientId)
        .single();

      if (error || !currentRecord) {
        throw error ?? new Error('Evolução não encontrada para assinatura.');
      }

      const currentSignature = getMedicalRecordSignatureMetadata(currentRecord.recordAttachments);
      if (currentSignature?.verificationToken) {
        toast.info('Esta evolução já foi assinada em outra sessão.');
        await fetchAndSetProntuarios();
        return;
      }

      const currentAttachments = getMedicalRecordAttachmentsObject(currentRecord.recordAttachments);
      const nextSignature = await buildSignedDocumentMetadata({
        hashPayload: {
          documentType: 'medical_record',
          recordId: prontuario.id,
          recordDate: currentRecord.recordDate,
          recordTitle: currentRecord.recordTitle,
          recordDescription: currentRecord.recordDescription,
          vitalSigns: getMedicalRecordVitalSigns(currentAttachments),
          patientId: prontuario.patientId,
          patientName: prontuario.patientFullName,
          patientMedicalRecordNumber: prontuario.medicalRecordNumber,
          signedByUserId: signer.userId,
          signedByName: signer.name,
          signedByRole: signer.professionalTitle,
          signedByRegistration: signer.councilNumber,
        },
        signedByUserId: user.userId,
        signedByName: signer.name,
        signedByRole: signer.professionalTitle,
        signedByRegistration: signer.councilNumber,
        templateVersion: 2,
      });

      const nextAttachments = {
        ...currentAttachments,
        authorSnapshot: signer,
        signature: nextSignature,
      };

      const updatedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('medical_records')
        .update({
          recordAttachments: nextAttachments,
          recordUpdatedAt: updatedAt,
        })
        .eq('recordId', prontuario.id)
        .eq('clientId', selectedClient.clientId);

      if (updateError) {
        throw updateError;
      }

      const nextProntuario: ProntuarioData = {
        ...prontuario,
        author: signer,
        signature: nextSignature,
        recordUpdatedAt: updatedAt,
      };

      setAllProntuarios((current) =>
        current.map((item) => (item.id === prontuario.id ? nextProntuario : item)),
      );
      setSelectedProntuario((current) =>
        current?.id === prontuario.id ? nextProntuario : current,
      );
      setEditingProntuario((current) =>
        current?.id === prontuario.id ? nextProntuario : current,
      );

      toast.success('Evolução assinada com sucesso. O QR code de autenticidade já está disponível.');
    } catch (error: any) {
      console.error('Erro ao assinar evolução:', error);
      const message =
        error?.message === 'SIGNED_MEDICAL_RECORD_LOCKED'
          ? 'Esta evolução já está assinada e bloqueada para edição.'
          : error?.message || 'Não foi possível assinar a evolução.';
      toast.error(message);
    } finally {
      setSigningEvolutionId(null);
    }
  }, [fetchAndSetProntuarios, selectedClient?.clientId, selectedClient?.userRole, user]);

  const openEvolutionEditor = async (
    prontuario: ProntuarioData,
    mode: EvolutionEditMode,
  ) => {
    if (mode === 'clinical' && prontuario.signature?.verificationToken) {
      toast.info('Esta evolução já está assinada e não pode mais ser editada.');
      return;
    }

    if (mode === 'appointment' && prontuario.status === 'invalidated') {
      toast.info('Evoluções invalidadas não permitem novo vínculo com agendamento.');
      return;
    }

    setEditMode(mode);
    setEditingProntuario(prontuario);
    setEditVitalSignsDraft(
      medicalRecordVitalSignsToDraft(prontuario.vitalSigns, prontuario.recordDate),
    );
    setEditForm({
      agendamentoId: prontuario.agendamentoId || '',
      ...(mode === 'clinical'
        ? {
            recordDate: prontuario.recordDate,
            recordDescription: extractTextFromHTML(prontuario.recordDescription || ''),
          }
        : {}),
    });

    if (prontuario.patientId) {
      await loadPatientAppointments(prontuario.patientId, prontuario.id);
    }
  };

  // Função para iniciar edição clínica
  const startEdit = async (prontuario: ProntuarioData) => {
    await openEvolutionEditor(prontuario, 'clinical');
  };

  const startAppointmentLinkEdit = async (prontuario: ProntuarioData) => {
    await openEvolutionEditor(prontuario, 'appointment');
  };

  // Função para cancelar edição
  const cancelEdit = () => {
    setEditingProntuario(null);
    setEditMode('clinical');
    setEditForm({});
    setEditVitalSignsDraft(createEmptyMedicalRecordVitalSignsDraft());
    setPatientAppointments([]); // Limpar lista de agendamentos
  };

  // Função para salvar edição
  const saveEdit = async () => {
    if (!editingProntuario || !editForm) return;
    if (!selectedClient?.clientId) {
      toast.error("Tenant não identificado para edição da evolução");
      return;
    }
    const isAppointmentOnlyMode = editMode === 'appointment';
    const isSigned = Boolean(editingProntuario.signature?.verificationToken);
    let persistedClinicalProntuario: ProntuarioData | null = null;

    if (isSigned && !isAppointmentOnlyMode) {
      toast.error("Esta evolução já está assinada e não pode mais ser editada.");
      return;
    }

    try {
      if (!isAppointmentOnlyMode) {
        const parsedVitalSigns = parseMedicalRecordVitalSignsDraft(
          editVitalSignsDraft,
          editForm.recordDate || editingProntuario.recordDate,
        );
        if (parsedVitalSigns.error) {
          toast.error(parsedVitalSigns.error);
          return;
        }

        const { data: currentRecord, error: currentRecordError } = await supabase
          .from('medical_records')
          .select('recordAttachments')
          .eq('recordId', editingProntuario.id)
          .eq('clientId', selectedClient.clientId)
          .single();

        if (currentRecordError || !currentRecord) {
          console.error('Erro ao carregar metadados da evolução para edição:', currentRecordError);
          toast.error('Não foi possível carregar os dados atuais da evolução.');
          return;
        }

        const currentAttachments = getMedicalRecordAttachmentsObject(currentRecord.recordAttachments);
        const attachmentsWithoutVitalSigns = { ...currentAttachments };
        delete attachmentsWithoutVitalSigns.vitalSigns;
        const nextAttachments = parsedVitalSigns.value
          ? { ...attachmentsWithoutVitalSigns, vitalSigns: parsedVitalSigns.value }
          : attachmentsWithoutVitalSigns;

        const recordUpdatedAt = new Date().toISOString();
        const updateData: Partial<SupabaseMedicalRecordRow> = {
          recordUpdatedAt,
          recordAttachments: nextAttachments,
        };

        if (editForm.recordDate) {
          updateData.recordDate = new Date(editForm.recordDate).toISOString();
        }
        if (editForm.recordDescription) {
          updateData.recordDescription = editForm.recordDescription;
        }

        if (editForm.patientId) {
          const selectedPatient = patients.find((p) => p.id === editForm.patientId);
          const editBlockReason = selectedPatient ? getPatientNewCareBlockReason(selectedPatient) : 'lifecycle';
          if (!selectedPatient || editBlockReason) {
            toast.error(
              editBlockReason === 'clinical_inactive'
                ? 'Paciente está inativo assistencialmente (alta/parado/óbito) e não pode receber novas evoluções.'
                : 'Paciente inativado/arquivado no cadastro não pode receber novas evoluções.'
            );
            return;
          }
        }

        const { data: updatedRecord, error: updateError } = await supabase
          .from("medical_records")
          .update(updateData)
          .eq("recordId", editingProntuario.id)
          .eq("clientId", selectedClient.clientId)
          .select('recordAttachments, recordDate, recordDescription, recordUpdatedAt')
          .single();

        if (updateError || !updatedRecord) {
          console.error("Erro ao atualizar evolução no Supabase:", updateError);
          toast.error("Erro ao atualizar evolução");
          return;
        }

        const persistedVitalSigns = getMedicalRecordVitalSigns(updatedRecord.recordAttachments);
        const expectedVitalSigns = parsedVitalSigns.value;
        const vitalSignsWerePersisted = areMedicalRecordVitalSignsEqual(
          persistedVitalSigns,
          expectedVitalSigns,
        );

        if (!vitalSignsWerePersisted) {
          console.error('Os sinais vitais retornados não correspondem aos dados enviados.', {
            expectedVitalSigns,
            persistedVitalSigns,
          });
          toast.error('A evolução não confirmou a gravação dos sinais vitais. Tente novamente.');
          return;
        }

        persistedClinicalProntuario = {
          ...editingProntuario,
          recordDate: updatedRecord.recordDate || editingProntuario.recordDate,
          recordDescription:
            updatedRecord.recordDescription ?? editingProntuario.recordDescription,
          recordUpdatedAt: updatedRecord.recordUpdatedAt || recordUpdatedAt,
          vitalSigns: persistedVitalSigns,
        };

        setAllProntuarios((current) =>
          current.map((item) =>
            item.id === persistedClinicalProntuario?.id ? persistedClinicalProntuario : item,
          ),
        );
        setSelectedProntuario((current) =>
          current?.id === persistedClinicalProntuario?.id ? persistedClinicalProntuario : current,
        );
      }

      // Lógica de revinculação de agendamento
      const oldAppointmentId = editingProntuario.agendamentoId || null;
      const rawNewAppointmentId = typeof editForm.agendamentoId === 'string' ? editForm.agendamentoId : '';
      const newAppointmentId =
        !rawNewAppointmentId || rawNewAppointmentId === 'none'
          ? null
          : rawNewAppointmentId;
      let linkUpdated = false;
      let linkOperationErrored = false;

      if (oldAppointmentId !== newAppointmentId) {
        try {
          if (oldAppointmentId && !newAppointmentId) {
            await IntegrationService.unlinkEvolutionFromAppointment(editingProntuario.id, oldAppointmentId);
            toast.success("Vínculo com agendamento removido!");
            linkUpdated = true;
          } else if (!oldAppointmentId && newAppointmentId && newAppointmentId !== "none") {
            if (!user?.userId) {
              throw new Error("Usuário não identificado para concluir o novo agendamento.");
            }

            const { data: appointmentData, error: appointmentFetchError } = await supabase
              .from("appointments")
              .select("appointmentStatus, appointmentPaymentOrigin")
              .eq("appointmentId", newAppointmentId)
              .eq("clientId", selectedClient.clientId)
              .single();

            if (appointmentFetchError || !appointmentData) {
              throw new Error(
                appointmentFetchError?.message ||
                "Não foi possível identificar o status do agendamento selecionado."
              );
            }

            const originalNewAppointmentStatus = appointmentData.appointmentStatus;
            await IntegrationService.linkEvolutionToAppointment(editingProntuario.id, newAppointmentId);

            if (originalNewAppointmentStatus === 'scheduled') {
              await AppointmentStatusService.completeAppointment(newAppointmentId, user.userId, editingProntuario.id);
              toast.success("Evolução vinculada e agendamento concluído com faturamento processado!");
            } else if (originalNewAppointmentStatus === 'no_evaluated') {
              const nowIso = new Date().toISOString();
              const { error: completeWithoutBillingError } = await supabase
                .from("appointments")
                .update({
                  appointmentStatus: 'completed',
                  appointmentEvolutionId: editingProntuario.id,
                  appointmentCompletedAt: nowIso,
                  appointmentUpdatedAt: nowIso,
                  appointmentPaymentOrigin: appointmentData.appointmentPaymentOrigin || 'avulsa',
                })
                .eq("appointmentId", newAppointmentId)
                .eq("clientId", selectedClient.clientId);

              if (completeWithoutBillingError) {
                throw completeWithoutBillingError;
              }

              toast.success("Evolução vinculada e agendamento concluído sem faturamento (origem: Não avaliado).");
            } else {
              throw new Error(
                `Status "${originalNewAppointmentStatus}" não permitido para este vínculo. Ajuste o agendamento na Agenda e tente novamente.`
              );
            }
            linkUpdated = true;
          } else if (oldAppointmentId && newAppointmentId && newAppointmentId !== "none") {
            const relinkResult = await IntegrationService.relinkEvolution(
              editingProntuario.id,
              oldAppointmentId,
              newAppointmentId,
            );
            toast.success("Vínculo de agendamento atualizado!");
            toast.info(
              relinkResult.previousAppointmentStatus === 'scheduled'
                ? 'O agendamento antigo voltou para "Agendado". Qualquer ajuste adicional deve ser feito na Agenda.'
                : 'O agendamento antigo voltou para "Não avaliado". Qualquer ajuste adicional deve ser feito na Agenda.'
            );
            linkUpdated = true;
          }
        } catch (linkError) {
          linkOperationErrored = true;
          console.error("Erro ao atualizar vínculo de agendamento:", linkError);
          const linkErrorMessage = linkError instanceof Error ? linkError.message : null;
          if (linkErrorMessage) {
            toast.warning(
              isAppointmentOnlyMode
                ? `Não foi possível alterar o vínculo do agendamento: ${linkErrorMessage}`
                : `Evolução atualizada, mas o vínculo não foi alterado: ${linkErrorMessage}`,
            );
          } else {
            toast.warning(
              isAppointmentOnlyMode
                ? "Houve erro ao alterar o vínculo do agendamento"
                : "Evolução atualizada, mas houve erro ao alterar o vínculo do agendamento",
            );
          }
        }
      }

      const shouldRefreshFromServer = isAppointmentOnlyMode || linkUpdated;
      const refreshedProntuarios = shouldRefreshFromServer
        ? await fetchAndSetProntuarios()
        : [];
      const refreshedProntuario = shouldRefreshFromServer
        ? refreshedProntuarios.find((item) => item.id === editingProntuario.id) || null
        : persistedClinicalProntuario;

      if (refreshedProntuario) {
        setSelectedProntuario((current) =>
          current?.id === refreshedProntuario.id ? refreshedProntuario : current,
        );
      }

      if (!isAppointmentOnlyMode) {
        toast.success("Evolução atualizada com sucesso!");
      } else if (!linkUpdated && !linkOperationErrored) {
        toast.info("Nenhuma alteração de vínculo foi necessária.");
      }

      setEditingProntuario(null);
      setEditMode('clinical');
      setEditForm({});
      setEditVitalSignsDraft(createEmptyMedicalRecordVitalSignsDraft());
      setPatientAppointments([]);
    } catch (error) {
      console.error("Erro ao salvar edição da evolução:", error);
      toast.error(editMode === 'appointment' ? "Erro ao salvar vínculo" : "Erro ao salvar alterações");
    }
  };

  // Função para atualizar campo do formulário
  const updateEditForm = (field: keyof ProntuarioData, value: any) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Contadores usados nos filtros de status.
  const stats = useMemo(() => ({
    total: allProntuarios.filter(p => p.status === 'active').length,
    invalidated: allProntuarios.filter(p => p.status === 'invalidated').length,
  }), [allProntuarios]);

  const statusFilterOptions: Array<{
    value: 'active' | 'invalidated' | 'all';
    label: string;
    count: number;
  }> = [
    { value: 'active', label: 'Ativos', count: stats.total },
    { value: 'invalidated', label: 'Invalidados', count: stats.invalidated },
    { value: 'all', label: 'Todos', count: allProntuarios.length },
  ];

  const professionalFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          allProntuarios
            .map((prontuario) => prontuario.author?.name?.trim())
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort((left, right) => left.localeCompare(right, 'pt-BR')),
    [allProntuarios],
  );

  const activeFiltersCount =
    (searchTerm.trim() ? 1 : 0) +
    (filterPatient !== 'all' ? 1 : 0) +
    (filterProfessional !== 'all' ? 1 : 0) +
    (dateFilter ? 1 : 0) +
    (statusFilter !== 'active' ? 1 : 0);

  const selectedPatientName =
    filterPatient !== 'all'
      ? patients.find((patient) => patient.id === filterPatient)?.personalData?.name || 'Paciente selecionado'
      : '';

  const formattedDateFilter = dateFilter
    ? new Date(`${dateFilter}T00:00:00`).toLocaleDateString('pt-BR')
    : '';

  const selectedProfessionalName =
    filterProfessional !== 'all'
      ? professionalFilterOptions.find((professionalName) => professionalName === filterProfessional) || 'Profissional selecionado'
      : '';

  const activeFilterChips = [
    searchTerm.trim()
      ? {
          key: 'search',
          label: `Busca: ${searchTerm.trim()}`,
          onRemove: () => setSearchTerm(''),
        }
      : null,
    filterPatient !== 'all'
      ? {
          key: 'patient',
          label: `Paciente: ${selectedPatientName}`,
          onRemove: () => setFilterPatient('all'),
        }
      : null,
    filterProfessional !== 'all'
      ? {
          key: 'professional',
          label: `Profissional: ${selectedProfessionalName}`,
          onRemove: () => setFilterProfessional('all'),
        }
      : null,
    dateFilter
      ? {
          key: 'date',
          label: `Data: ${formattedDateFilter}`,
          onRemove: () => setDateFilter(''),
        }
      : null,
    statusFilter !== 'active'
      ? {
          key: 'status',
          label: `Status: ${statusFilter === 'all' ? 'Todos' : 'Invalidados'}`,
          onRemove: () => setStatusFilter('active'),
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }>;

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterPatient('all');
    setFilterProfessional('all');
    setDateFilter('');
    setStatusFilter('active');
  };

  const toggleBatchPrintSelectionMode = () => {
    if (!canUseBatchPrint) {
      toast.info('Selecione um paciente para habilitar a impressão em lote.');
      return;
    }

    setShowBatchPrintSelection((current) => !current);
    setSelectedBatchPrintIds([]);
  };

  const toggleBatchPrintItem = (prontuarioId: string) => {
    setSelectedBatchPrintIds((current) =>
      current.includes(prontuarioId)
        ? current.filter((id) => id !== prontuarioId)
        : [...current, prontuarioId],
    );
  };

  const toggleSelectAllVisibleProntuarios = () => {
    if (paginatedProntuarios.length === 0) {
      return;
    }

    setSelectedBatchPrintIds(
      allVisibleProntuariosSelected ? [] : paginatedProntuarios.map((prontuario) => prontuario.id),
    );
  };

  const handleBatchPrint = async () => {
    if (!canUseBatchPrint) {
      toast.info('Selecione um paciente para habilitar a impressão em lote.');
      return;
    }

    if (selectedBatchPrintProntuarios.length === 0) {
      toast.info('Selecione ao menos uma evolução para imprimir.');
      return;
    }

    try {
      setIsBatchPrinting(true);
      await printEvolutions(selectedBatchPrintProntuarios);
      setSelectedBatchPrintIds([]);
      setShowBatchPrintSelection(false);
    } finally {
      setIsBatchPrinting(false);
    }
  };

  // Exportar para CSV
  const exportToCSV = () => {
    const headers = [
      'Data da evolução',
      'Paciente',
      'Evolução',
      'Data de Criação'
    ];

    const csvData = filteredProntuarios.map(prontuario => [
      formatDate(prontuario.recordDate, true),
      prontuario.patientFullName || 'Nome não disponível',
      extractTextFromHTML(prontuario.recordDescription || ''),
      formatDate(prontuario.recordCreatedAt, true)
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `evolucoes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <div className="ml-4">
          <p className="text-gray-600">Carregando evoluções...</p>
          <p className="text-sm text-gray-500">Sincronizando registros clínicos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Exibir erro de conexão se houver */}
      {connectionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro ao carregar evoluções</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{connectionError}</p>
                <p className="mt-2">Verifique a conexão, o cliente selecionado e tente atualizar a página.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notificação de Atualização */}
      {showUpdateNotification && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg shadow-md animate-fade-in">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <RefreshCw className="h-5 w-5 text-blue-400 animate-spin" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-800">
                Sistema atualizado automaticamente! {newItemsCount} novo(s) registro(s) disponível(is).
              </p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setShowUpdateNotification(false)}
                className="inline-flex text-blue-400 hover:text-blue-600 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evoluções clínicas</h1>
          <p className="text-gray-600">Registre, consulte e acompanhe evoluções dos pacientes</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ActionButton
            onClick={refreshData}
            variant="neutral"
            className="whitespace-nowrap px-3 md:px-4 md:text-base"
            disabled={isRefreshing}
            title="Atualizar dados"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isRefreshing ? 'Atualizando...' : 'Atualizar'}</span>
          </ActionButton>
          <ActionButton
            onClick={() => {
              setShowLastEvolutionDetails(false);
              setShowNewProntuarioModal(true);
              if (filterPatient !== 'all') {
                void handlePatientSelection(filterPatient);
              }
            }}
            variant="primary"
            className="whitespace-nowrap px-3 md:px-4 md:text-base"
          >
            <PlusCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Nova evolução</span>
          </ActionButton>
          <ActionButton
            onClick={exportToCSV}
            variant="success"
            className="whitespace-nowrap px-3 md:px-4 md:text-base"
            disabled={filteredProntuarios.length === 0}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Exportar CSV</span>
          </ActionButton>
        </div>
      </div>

      {filterPatient !== 'all' && (
        <div className="inline-flex w-full rounded-xl border border-slate-200 bg-slate-100 p-1 sm:w-auto">
          <button
            type="button"
            onClick={() => setMedicalRecordView('evolutions')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors sm:flex-none ${
              medicalRecordView === 'evolutions'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <FileText className="h-4 w-4" />
            Evoluções
          </button>
          <button
            type="button"
            onClick={() => setMedicalRecordView('timeline')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors sm:flex-none ${
              medicalRecordView === 'timeline'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <History className="h-4 w-4" />
            Timeline clínica
          </button>
        </div>
      )}

      {medicalRecordView === 'timeline' &&
      filterPatient !== 'all' &&
      selectedClient?.clientId &&
      user?.userId ? (
        <PatientClinicalTimeline
          key={`${filterPatient}-${timelineRefreshVersion}`}
          patientId={filterPatient}
          clientId={selectedClient.clientId}
          patientName={selectedPatientName}
          userId={user.userId}
          userDisplayName={user.userDisplayName}
        />
      ) : (
        <>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm md:min-w-[460px]">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">{filteredProntuarios.length}</span> evoluções encontradas
            </p>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            loading={loading}
            isLoadingAll={pageSize === 'all'}
            onNextPage={() => setCurrentPage(prev => prev + 1)}
            onPreviousPage={() => setCurrentPage(prev => prev - 1)}
            onFirstPage={() => setCurrentPage(1)}
            onPageSizeChange={handlePageSizeChange}
            variant="inline"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowFilters((current) => !current)}
          className="inline-flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:bg-gray-50"
        >
          <span className="flex items-center gap-3">
            <span className="rounded-md bg-gray-100 p-2">
              <Filter className="h-4 w-4 text-gray-600" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-gray-900">Filtros</span>
              <span className="block text-xs text-gray-500">
                {activeFiltersCount > 0
                  ? `${activeFiltersCount} filtro(s) ativo(s)`
                  : 'Sem filtros ativos'}
              </span>
            </span>
          </span>
          {showFilters ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </button>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          {activeFilterChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700"
            >
              <span>{chip.label}</span>
              <button
                type="button"
                onClick={chip.onRemove}
                className="text-gray-400 transition-colors hover:text-gray-600"
                aria-label={`Remover filtro ${chip.label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
          >
            Limpar filtros
          </button>
        </div>
      )}

      {canUseBatchPrint && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Impressão em lote</p>
              <p className="text-xs text-gray-500">
                Selecione várias evoluções de {selectedPatientName || 'um paciente'} e gere uma única impressão.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={toggleBatchPrintSelectionMode}
                className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  showBatchPrintSelection
                    ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {showBatchPrintSelection ? 'Cancelar seleção' : 'Selecionar para imprimir'}
              </button>

              {showBatchPrintSelection && (
                <>
                  <button
                    type="button"
                    onClick={toggleSelectAllVisibleProntuarios}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    {allVisibleProntuariosSelected ? 'Limpar página' : 'Selecionar página'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleBatchPrint();
                    }}
                    disabled={selectedBatchPrintProntuarios.length === 0 || isBatchPrinting}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Printer className="h-4 w-4" />
                    {isBatchPrinting ? 'Preparando...' : 'Imprimir selecionadas'}
                  </button>
                </>
              )}
            </div>
          </div>

          {showBatchPrintSelection && (
            <p className="mt-3 text-xs text-gray-500">
              {selectedBatchPrintProntuarios.length} evolução(ões) selecionada(s) na página atual.
            </p>
          )}
        </div>
      )}

      {showFilters && (
        <div className="bg-white p-5 rounded-lg shadow-sm border">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Filtros</h3>
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <X className="w-4 h-4" />
              Limpar
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Paciente, profissional, evolução ou documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Paciente</label>
              <PatientSearchSelect
                patients={[{ id: 'all', personalData: { name: 'Todos os pacientes' } }, ...patients] as UnifiedPatient[]}
                selectedPatientId={filterPatient}
                onSelect={(id) => setFilterPatient(id)}
                placeholder="Todos os pacientes"
                showActiveFilter={false}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Profissional</label>
              <select
                value={filterProfessional}
                onChange={(e) => setFilterProfessional(e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos os profissionais</option>
                {professionalFilterOptions.map((professionalName) => (
                  <option key={professionalName} value={professionalName}>
                    {professionalName}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-12">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status do registro</label>
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => {
                  const isActive = statusFilter === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStatusFilter(option.value)}
                      className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span>{option.label}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {option.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de evoluções */}
      <div className="overflow-hidden rounded-lg border bg-white shadow-sm md:hidden">
        <div className="divide-y divide-gray-100">
          {paginatedProntuarios.map((prontuario) => {
            const isInvalidated = prontuario.status === 'invalidated';
            const isSigned = Boolean(prontuario.signature?.verificationToken);
            const evolutionPreview = extractTextFromHTML(prontuario.recordDescription || '');
            const isSelectedForBatchPrint = selectedBatchPrintIds.includes(prontuario.id);

            return (
              <div key={prontuario.id} className="flex items-start gap-3 px-4 py-3">
                {showBatchPrintSelection && canUseBatchPrint && (
                  <label className="mt-1 flex shrink-0 items-center">
                    <input
                      type="checkbox"
                      checked={isSelectedForBatchPrint}
                      onChange={() => toggleBatchPrintItem(prontuario.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedProntuario(prontuario)}
                  className="w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {prontuario.patientFullName || 'Nome não disponível'}
                        </p>
                        {isSigned && (
                          <span className="inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 ring-1 ring-sky-100">
                            Assinada
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-600" title={evolutionPreview}>
                        {evolutionPreview || 'Sem texto registrado'}
                      </p>
                      <div className="mt-1 flex items-center justify-between gap-3">
                        <p className="truncate text-[11px] text-gray-500">
                          {prontuario.author?.name || 'Profissional não informado'}
                        </p>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          isInvalidated
                            ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
                            : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                        }`}>
                          {isInvalidated && <AlertTriangle className="h-3 w-3" />}
                          {isInvalidated ? 'Invalidada' : 'Ativa'}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-xs font-medium text-gray-700">{formatCompactDate(prontuario.recordDate)}</span>
                      <ChevronRight className="h-4 w-4 text-gray-300" />
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden overflow-hidden rounded-lg border bg-white shadow-sm md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {showBatchPrintSelection && canUseBatchPrint && (
                  <th className="w-12 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <input
                      type="checkbox"
                      checked={allVisibleProntuariosSelected}
                      onChange={toggleSelectAllVisibleProntuarios}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label="Selecionar evoluções da página"
                    />
                  </th>
                )}
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('paciente')}
                >
                  <div className="flex items-center gap-2">
                    Paciente
                    {getSortIcon('paciente')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('data')}
                >
                  <div className="flex items-center gap-2">
                    Data
                    {getSortIcon('data')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Profissional
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Evolução
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {paginatedProntuarios.map((prontuario) => {
                const isInvalidated = prontuario.status === 'invalidated';
                const isSigned = Boolean(prontuario.signature?.verificationToken);
                const evolutionPreview = extractTextFromHTML(prontuario.recordDescription || '');
                const actionIconClassName = 'inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50';
                const isSelectedForBatchPrint = selectedBatchPrintIds.includes(prontuario.id);

                return (
                  <tr key={prontuario.id} className="hover:bg-gray-50">
                    {showBatchPrintSelection && canUseBatchPrint && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelectedForBatchPrint}
                          onChange={() => toggleBatchPrintItem(prontuario.id)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label={`Selecionar evolução de ${prontuario.patientFullName || 'paciente'}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {prontuario.patientFullName || 'Nome não disponível'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatDate(prontuario.recordDate, true)}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">{prontuario.author?.name || 'Não informado'}</p>
                      <p className="text-xs text-gray-500">{prontuario.author?.professionalTitle || 'Responsável clínico'}</p>
                    </td>
                    <td className="max-w-sm px-4 py-3">
                      <p className="truncate text-sm text-gray-600" title={evolutionPreview}>
                        {evolutionPreview || 'Sem texto registrado'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                          isInvalidated
                            ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
                            : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                        }`}>
                          {isInvalidated && <AlertTriangle className="h-3 w-3" />}
                          {isInvalidated ? 'Invalidada' : 'Ativa'}
                        </span>
                        {isSigned && (
                          <span className="inline-flex items-center rounded-full bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 ring-1 ring-sky-100">
                            Assinada
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSelectedProntuario(prontuario)}
                          className={actionIconClassName}
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!isInvalidated && !isSigned && (
                          <button
                            onClick={() => startEdit(prontuario)}
                            className={actionIconClassName}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => { void printEvolution(prontuario); }}
                          className={actionIconClassName}
                          title="Imprimir"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                        {!isInvalidated && (
                          isSigned ? (
                            <>
                              <button
                                onClick={() => openVerificationPage(prontuario.signature!.verificationToken)}
                                className={actionIconClassName}
                                title="Conferir autenticidade"
                              >
                                <ShieldCheck className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => { void startAppointmentLinkEdit(prontuario); }}
                                className={actionIconClassName}
                                title="Gerenciar vínculo da sessão"
                              >
                                <Link2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => void handleSignEvolution(prontuario)}
                              disabled={signingEvolutionId === prontuario.id}
                              className={actionIconClassName}
                              title="Assinar eletronicamente"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )
                        )}
                        {!isInvalidated && (
                          <button
                            onClick={() => setInvalidatingEvolution(prontuario)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                            title="Invalidar"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </button>
                        )}
                        {isInvalidated && prontuario.invalidation_history && prontuario.invalidation_history.length > 0 && (
                          <button
                            onClick={() => setSelectedProntuario(prontuario)}
                            className={actionIconClassName}
                            title="Ver histórico"
                          >
                            <History className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Modal de nova evolução */}
      {showNewProntuarioModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Nova evolução</h2>
                <button
                  onClick={closeNewEvolutionModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paciente *
                  </label>
                  <PatientSearchSelect
                    patients={patients}
                    selectedPatientId={newProntuarioForm.selectedPatientId}
                    onSelect={handlePatientSelection}
                    placeholder="Buscar paciente por nome ou prontuário..."
                    showActiveFilter={false}
                    allowInactiveSelection={false}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Data e hora da evolução *
                    </label>
                    <input
                      type="datetime-local"
                      value={newProntuarioForm.datetime}
                      onChange={(e) => setNewProntuarioForm(prev => ({ ...prev, datetime: e.target.value }))}
                      className="h-11 w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {newProntuarioForm.selectedPatientId && (
                    <div ref={appointmentPickerRef} className="relative">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Agendamento <span className="font-normal text-gray-400">(opcional)</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowAppointmentPicker((current) => !current)}
                        className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 text-left shadow-sm transition-colors hover:border-gray-400"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <CalendarDays className="h-4 w-4 shrink-0 text-gray-500" />
                          <span className="truncate text-sm text-gray-800">
                            {selectedAppointment
                              ? `${new Date(selectedAppointment.start).toLocaleDateString('pt-BR')} às ${new Date(selectedAppointment.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}${selectedAppointment.location ? ` · ${selectedAppointment.location}` : ''}`
                              : newProntuarioForm.selectedAppointmentId === 'none'
                                ? 'Sem agendamento'
                                : 'Vincular'}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-semibold text-blue-600">
                          {selectedAppointment ? 'Alterar' : 'Selecionar'}
                        </span>
                      </button>

                      {showAppointmentPicker && (
                        <div className="absolute right-0 top-full z-30 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl sm:w-[430px]">
                          <div className="border-b border-gray-100 p-3">
                            <div className="inline-flex w-full rounded-lg bg-gray-100 p-1 text-xs font-semibold">
                              <button
                                type="button"
                                onClick={() => setAppointmentScope('mine')}
                                className={`flex-1 rounded-md px-3 py-2 transition-colors ${appointmentScope === 'mine' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                              >
                                Meus agendamentos
                              </button>
                              <button
                                type="button"
                                onClick={() => setAppointmentScope('all')}
                                className={`flex-1 rounded-md px-3 py-2 transition-colors ${appointmentScope === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                              >
                                Todos do paciente
                              </button>
                            </div>
                          </div>

                          <div className="max-h-72 overflow-y-auto p-2">
                            {loadingAppointments ? (
                              <div className="flex items-center gap-2 p-3 text-sm text-gray-600">
                                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-500" />
                                Carregando agendamentos...
                              </div>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewProntuarioForm((current) => ({ ...current, selectedAppointmentId: 'none' }));
                                    setShowAppointmentPicker(false);
                                  }}
                                  className="w-full rounded-lg px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                                >
                                  Continuar sem agendamento vinculado
                                </button>

                                {visibleCategorizedAppointments.currentLink && (
                                  <div className="mt-2 border-t border-gray-100 pt-2">
                                    <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Pré-selecionado</p>
                                    {[visibleCategorizedAppointments.currentLink].map((appointment) => (
                                      <button
                                        key={appointment.id}
                                        type="button"
                                        onClick={() => {
                                          setNewProntuarioForm((current) => ({ ...current, selectedAppointmentId: appointment.id }));
                                          setShowAppointmentPicker(false);
                                        }}
                                        className="w-full rounded-lg bg-blue-50 px-3 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-100"
                                      >
                                        {new Date(appointment.start).toLocaleDateString('pt-BR')} às {new Date(appointment.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}{appointment.location ? ` · ${appointment.location}` : ''}
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {visibleCategorizedAppointments.available.length > 0 && (
                                  <div className="mt-2 border-t border-gray-100 pt-2">
                                    <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Disponíveis</p>
                                    {visibleCategorizedAppointments.available.map((appointment) => (
                                      <button
                                        key={appointment.id}
                                        type="button"
                                        onClick={() => {
                                          setNewProntuarioForm((current) => ({ ...current, selectedAppointmentId: appointment.id }));
                                          setShowAppointmentPicker(false);
                                        }}
                                        className="w-full rounded-lg px-3 py-2 text-left text-sm text-gray-800 hover:bg-emerald-50"
                                      >
                                        {new Date(appointment.start).toLocaleDateString('pt-BR')} às {new Date(appointment.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}{appointment.location ? ` · ${appointment.location}` : ''}
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {visibleCategorizedAppointments.linked.length > 0 && (
                                  <details className="mt-2 border-t border-gray-100 pt-2">
                                    <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-500">
                                      Já vinculados ({visibleCategorizedAppointments.linked.length})
                                    </summary>
                                    {visibleCategorizedAppointments.linked.map((appointment) => (
                                      <div key={appointment.id} className="px-3 py-2 text-xs text-gray-400">
                                        {new Date(appointment.start).toLocaleDateString('pt-BR')} às {new Date(appointment.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}{appointment.location ? ` · ${appointment.location}` : ''}
                                      </div>
                                    ))}
                                  </details>
                                )}

                                {patientAppointments.length === 0 && (
                                  <p className="p-3 text-xs text-gray-500">Nenhum agendamento encontrado nos últimos 180 dias.</p>
                                )}
                                {patientAppointments.length > 0 && !hasAppointmentsFromCurrentUser && appointmentScope === 'all' && (
                                  <p className="mt-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                                    Nenhum agendamento no seu nome. Exibindo todos os agendamentos do paciente.
                                  </p>
                                )}
                                {visibleCategorizedAppointments.available.length === 0 && visiblePatientAppointmentsCount > 0 && (
                                  <p className="p-3 text-xs text-gray-500">Não há agendamentos disponíveis para vínculo nesta lista.</p>
                                )}
                              </>
                            )}
                          </div>
                          <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
                            O vínculo controla sessões e faturamento do atendimento.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {newProntuarioForm.selectedPatientId && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {user && selectedClient && (
                      <InitialAnamnesisPanel
                        key={`${newProntuarioForm.selectedPatientId}-${timelineRefreshVersion}`}
                        patientId={newProntuarioForm.selectedPatientId}
                        patientName={patients.find((patient) => patient.id === newProntuarioForm.selectedPatientId)?.personalData?.name}
                        user={user}
                        selectedClient={selectedClient}
                        onCopy={copyClinicalHistory}
                        onSaved={() => setTimelineRefreshVersion((current) => current + 1)}
                      />
                    )}
                    {user && selectedClient && (
                      <ClinicalRecordShortcutMenu
                        patientId={newProntuarioForm.selectedPatientId}
                        patientName={patients.find((patient) => patient.id === newProntuarioForm.selectedPatientId)?.personalData?.name}
                        user={user}
                        selectedClient={selectedClient}
                        onSaved={() => setTimelineRefreshVersion((current) => current + 1)}
                        onStartAssessment={() => openNewAssessmentForPatient(newProntuarioForm.selectedPatientId)}
                      />
                    )}
                  </div>
                )}

                <div>
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Evolução *
                    </label>
                    <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                      {showLastEvolution && (
                        <button
                          type="button"
                          onClick={() => setShowLastEvolutionDetails(true)}
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100"
                        >
                          <History className="h-3.5 w-3.5" />
                          Última evolução
                        </button>
                      )}
                      <EvolutionVitalSignsField
                        value={newEvolutionVitalSignsDraft}
                        onChange={setNewEvolutionVitalSignsDraft}
                        fallbackMeasuredAt={newProntuarioForm.datetime}
                        disabled={isSubmittingProntuario}
                      />
                    </div>
                  </div>
                  <textarea
                    value={newProntuarioForm.evolution}
                    onChange={(e) => setNewProntuarioForm(prev => ({ ...prev, evolution: e.target.value }))}
                    rows={8}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Digite a evolução do paciente..."
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <ActionButton
                  onClick={closeNewEvolutionModal}
                  variant="neutral"
                  className="px-6"
                  disabled={isSubmittingProntuario}
                >
                  Cancelar
                </ActionButton>
                <ActionButton
                  onClick={saveNewProntuario}
                  variant="primary"
                  className="px-6"
                  disabled={isSubmittingProntuario}
                >
                  {isSubmittingProntuario ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvar evolução
                    </>
                  )}
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNewProntuarioModal && showLastEvolutionDetails && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 p-5">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Última evolução</h2>
                <p className="mt-1 text-sm text-gray-500">Use como referência para o atendimento atual.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowLastEvolutionDetails(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-5">
              <div className="whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm leading-6 text-gray-700">
                {lastEvolution}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
              <ActionButton
                type="button"
                onClick={() => setShowLastEvolutionDetails(false)}
                variant="neutral"
              >
                Fechar
              </ActionButton>
              <ActionButton
                type="button"
                onClick={copyLastEvolution}
                variant="primary"
              >
                <Copy className="h-4 w-4" />
                Copiar para evolução
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {selectedProntuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="break-words text-xl font-bold text-gray-900 sm:text-2xl">
                    Evolução - {selectedProntuario.patientFullName || 'Nome não disponível'}
                  </h2>
                  <p className="text-gray-600">
                    {formatDate(selectedProntuario.recordDate, true)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProntuario(null)}
                  className="shrink-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {selectedProntuario.signature?.verificationToken && (
                  <div className="bg-sky-50 border-l-4 border-sky-400 p-4">
                    <div className="flex">
                      <FileText className="h-5 w-5 text-sky-500 mr-2" />
                      <div>
                        <h3 className="text-sm font-medium text-sky-900">Documento assinado eletronicamente</h3>
                        <div className="mt-2 text-sm text-sky-800">
                          <p><strong>Código:</strong> {selectedProntuario.signature.verificationToken}</p>
                          <p><strong>Assinado em:</strong> {formatDate(selectedProntuario.signature.signedAt, true)}</p>
                          <p><strong>Assinado por:</strong> {selectedProntuario.signature.signedByName}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedProntuario.status === 'invalidated' && (
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                      <div>
                        <h3 className="text-sm font-medium text-red-800">Este registro está invalidado</h3>
                        {selectedProntuario.invalidation_info && (
                          <div className="mt-2 text-sm text-red-700">
                            <p><strong>Justificativa:</strong> {selectedProntuario.invalidation_info.justification}</p>
                            <p><strong>Invalidado por:</strong> {selectedProntuario.invalidation_info.user_name}</p>
                            <p><strong>Data:</strong> {formatDate(selectedProntuario.invalidation_info.timestamp, true)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Evolução</h3>
                  <div className="break-words whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
                    {selectedProntuario.vitalSigns && (
                      <div className="mb-3 border-b border-gray-200 pb-2 font-medium text-gray-700">
                        <strong>Sinais vitais:</strong>{' '}
                        {formatMedicalRecordVitalSigns(selectedProntuario.vitalSigns, { includeMeasuredAt: true })}
                      </div>
                    )}
                    {extractTextFromHTML(selectedProntuario.recordDescription || '')}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                  <div>
                    <span className="font-medium text-gray-600">Data de Criação:</span>
                    <p>{formatDate(selectedProntuario.recordCreatedAt, true)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">ID do Documento:</span>
                    <p className="break-all font-mono text-xs">{selectedProntuario.id}</p>
                  </div>
                </div>

                {selectedProntuario.invalidation_history && selectedProntuario.invalidation_history.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <History className="w-5 h-5 mr-2" />
                      Histórico de Invalidações
                    </h3>
                    <div className="space-y-3">
                      {selectedProntuario.invalidation_history.map((inv, index) => (
                        <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-semibold text-gray-500">
                              Invalidação #{selectedProntuario.invalidation_history!.length - index}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(inv.timestamp, true)}
                            </span>
                          </div>
                          <div className="text-sm space-y-1">
                            <p><strong className="text-gray-600">Usuário:</strong> {inv.user_name}</p>
                            <p><strong className="text-gray-600">Email:</strong> {inv.user_email}</p>
                            <p><strong className="text-gray-600">Justificativa:</strong></p>
                            <p className="text-gray-700 pl-4 italic">{inv.justification}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3">
                <ActionButton
                  onClick={() => printEvolution(selectedProntuario)}
                  variant="neutral"
                  className="w-full sm:w-auto"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </ActionButton>
                {selectedProntuario.signature?.verificationToken ? (
                  <>
                    <ActionButton
                      onClick={() => openVerificationPage(selectedProntuario.signature!.verificationToken)}
                      variant="secondary"
                      className="w-full sm:w-auto"
                    >
                      <Eye className="w-4 h-4" />
                      Conferir autenticidade
                    </ActionButton>
                    {selectedProntuario.status !== 'invalidated' && (
                      <>
                        <ActionButton
                          onClick={() => { void startAppointmentLinkEdit(selectedProntuario); }}
                          variant="neutral"
                          className="w-full sm:w-auto"
                        >
                          <Link2 className="w-4 h-4" />
                          Gerenciar vínculo
                        </ActionButton>
                        <ActionButton
                          onClick={() => setInvalidatingEvolution(selectedProntuario)}
                          variant="danger"
                          className="w-full sm:w-auto"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          Invalidar
                        </ActionButton>
                      </>
                    )}
                  </>
                ) : selectedProntuario.status !== 'invalidated' ? (
                  <>
                    <ActionButton
                      onClick={() => void handleSignEvolution(selectedProntuario)}
                      disabled={signingEvolutionId === selectedProntuario.id}
                      variant="success"
                      className="w-full sm:w-auto"
                    >
                      <FileText className="w-4 h-4" />
                      Assinar
                    </ActionButton>
                    <ActionButton
                      onClick={() => startEdit(selectedProntuario)}
                      variant="secondary"
                      className="w-full sm:w-auto"
                    >
                      <Edit className="w-5 h-5" />
                      Editar
                    </ActionButton>
                    <ActionButton
                      onClick={() => setInvalidatingEvolution(selectedProntuario)}
                      variant="danger"
                      className="w-full sm:w-auto"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Invalidar
                    </ActionButton>
                  </>
                ) : null}
                <ActionButton
                  onClick={() => setSelectedProntuario(null)}
                  variant="neutral"
                  className="w-full bg-slate-100 sm:w-auto"
                >
                  Fechar
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {editingProntuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editMode === 'appointment' ? 'Gerenciar vínculo da sessão' : 'Editar evolução'} - {editingProntuario.patientFullName || 'Nome não disponível'}
                </h2>
                <button
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {editMode === 'appointment' && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
                    <div className="font-semibold">Documento assinado</div>
                    <p className="mt-1">
                      O conteúdo clínico desta evolução está bloqueado. Aqui você pode ajustar apenas o vínculo com o agendamento.
                    </p>
                  </div>
                )}

                {editMode === 'clinical' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data e hora da evolução
                    </label>
                    <input
                      type="datetime-local"
                      value={editForm.recordDate ? new Date(editForm.recordDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => updateEditForm('recordDate', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* 🆕 Dropdown de Agendamentos no modo de edição */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    🔗 Gerenciar vínculo com agendamento
                  </label>
                  {editingProntuario.agendamentoId && (
                    <div className="mb-3 p-2 bg-blue-100 border border-blue-300 rounded text-sm text-blue-800">
                      <strong>Vínculo atual:</strong> Agendamento ID: {editingProntuario.agendamentoId.substring(0, 8)}...
                    </div>
                  )}
                  {loadingAppointments ? (
                    <div className="flex items-center gap-2 text-blue-700">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                      <span className="text-sm">Carregando agendamentos...</span>
                    </div>
                  ) : (
                    <>
                      <select
                        value={editForm.agendamentoId || ''}
                        onChange={(e) => updateEditForm('agendamentoId', e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white"
                      >
                        <option value="">Selecione um agendamento...</option>
                        <option value="none">❌ Remover vínculo (evolução avulsa)</option>

                        {/* Vínculo Atual (se existir) */}
                        {categorizedAppointments.currentLink && (
                          <>
                            <option disabled className="font-bold bg-blue-50">
                              ─── 📌 VÍNCULO ATUAL ───
                            </option>
                            {(() => {
                              const appointment = categorizedAppointments.currentLink;
                              const date = new Date(appointment.start);
                              const dateStr = date.toLocaleDateString('pt-BR');
                              const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              const location = appointment.location ? ` - ${appointment.location}` : '';

                              return (
                                <option key={appointment.id} value={appointment.id} className="text-blue-800 font-bold">
                                  ★ {dateStr} às {timeStr}{location} (atual)
                                </option>
                              );
                            })()}
                          </>
                        )}

                        {/* Seção: Sessões Disponíveis para Vínculo */}
                        {categorizedAppointments.available.length > 0 && (
                          <>
                            <option disabled className="font-bold bg-green-50">
                              ─── ✅ SEÇÕES DISPONÍVEIS ({categorizedAppointments.available.length}) ───
                            </option>
                            {categorizedAppointments.available.map((appointment) => {
                              const date = new Date(appointment.start);
                              const dateStr = date.toLocaleDateString('pt-BR');
                              const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              const statusIcon = appointment.status === 'no_evaluated' ? '🟡' : '📅';
                              const statusLabel = appointment.status === 'no_evaluated' ? 'no_evaluated' : 'scheduled';
                              const location = appointment.location ? ` - ${appointment.location}` : '';

                              return (
                                <option key={appointment.id} value={appointment.id} className="text-green-800 font-medium">
                                  {statusIcon} {dateStr} às {timeStr}{location} [{statusLabel}]
                                </option>
                              );
                            })}
                          </>
                        )}

                        {/* Seção: Seções Avaliadas (limitadas a 12) */}
                        {categorizedAppointments.linked.length > 0 && (
                          <>
                            <option disabled className="font-bold bg-gray-50">
                              ─── 🧾 SEÇÕES AVALIADAS (últimas {categorizedAppointments.linked.length}) ───
                            </option>
                            {categorizedAppointments.linked.map((appointment) => {
                              const date = new Date(appointment.start);
                              const dateStr = date.toLocaleDateString('pt-BR');
                              const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              const statusIcon = appointment.status === 'no_billed' ? '🟣' : '✅';
                              const statusLabel = appointment.status === 'no_billed' ? 'no_billed' : 'completed';
                              const location = appointment.location ? ` - ${appointment.location}` : '';

                              return (
                                <option key={appointment.id} value={appointment.id} className="text-gray-600" disabled>
                                  {statusIcon} {dateStr} às {timeStr}{location} [{statusLabel}]
                                </option>
                              );
                            })}
                          </>
                        )}
                      </select>

                      {/* Informações e Estatísticas */}
                      <div className="mt-2 space-y-1">
                        {patientAppointments.length === 0 && (
                          <p className="text-xs text-blue-700">
                            ℹ️ Nenhum agendamento encontrado nos últimos 180 dias
                          </p>
                        )}
                        {categorizedAppointments.available.length > 0 && (
                          <p className="text-xs text-green-700 font-medium">
                            ✅ {categorizedAppointments.available.length} seção(ões) disponível(is) para correção de vínculo
                          </p>
                        )}
                        {categorizedAppointments.available.length === 0 && patientAppointments.length > 0 && !categorizedAppointments.currentLink && (
                          <p className="text-xs text-orange-700">
                            ⚠️ Nenhuma sessão disponível para vínculo (somente status scheduled e no_evaluated)
                          </p>
                        )}
                        <p className="text-xs text-blue-700">
                          💡 Altere o vínculo se a evolução foi registrada para o agendamento errado
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {editMode === 'clinical' && (
                  <div>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Evolução
                      </label>
                      <EvolutionVitalSignsField
                        value={editVitalSignsDraft}
                        onChange={setEditVitalSignsDraft}
                        fallbackMeasuredAt={editForm.recordDate || editingProntuario.recordDate}
                      />
                    </div>
                    <textarea
                      value={editForm.recordDescription || ''}
                      onChange={(e) => updateEditForm('recordDescription', e.target.value)}
                      rows={8}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Digite a evolução do paciente..."
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={cancelEdit}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveEdit}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editMode === 'appointment' ? 'Salvar vínculo' : 'Salvar alterações'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paginatedProntuarios.length === 0 && !loading && !connectionError && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhuma evolução encontrada</p>
          <p className="text-sm text-gray-500">Ajuste os filtros ou crie uma nova evolução</p>
        </div>
      )}

      {/* Modal de Invalidação */}
      {invalidatingEvolution && (
        <InvalidateEvolutionModal
          evolution={invalidatingEvolution}
          onClose={() => setInvalidatingEvolution(null)}
          onConfirm={handleInvalidateEvolution}
        />
      )}

      <AppointmentConflictResolutionModal
        isOpen={Boolean(appointmentConflictState)}
        candidates={appointmentConflictState?.candidates || []}
        recommendedAppointmentId={appointmentConflictState?.recommendedAppointmentId}
        onCancel={() => resolveConflictSelection(null)}
        onConfirm={(appointmentId) => resolveConflictSelection(appointmentId)}
      />
    </div>
  );
};

export default ProntuarioPage;
